/**
 * Live 3D renderer (three.js). Reads world state every frame and NEVER mutates.
 *
 * Terrain/water/hex-lines are built ONCE from world.hexes (static). Dynamic
 * entities (resource nodes, buildings, units) are synced each draw via an
 * eid→mesh pool (create on first sight, update transform on move, remove on
 * death). Selection rings + a build-placement ghost overlay on top.
 *
 * Camera: locked Northgard-style angle (fairness: all players share it);
 * WASD/arrows/edge pan + wheel zoom. All pure presentation — no sim imports
 * beyond type-only reads.
 */
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import type { World } from "../sim/world";
import type { Eid } from "../sim/types";
import type { MoveIntent } from "../sim/components";
import { hexCorners, type Biome, type Hex } from "../sim/hex";
import { propRadius } from "../sim/systems/movement";
import {
  GATE_ARM_INNER_RATIO,
  GATE_PIER_HALF_DEPTH_RATIO,
  GATE_PIER_HALF_WIDTH_RATIO,
} from "../sim/ramparts";
import { ResourceLayer } from "./resourceLayer";
import { Fog } from "./fog";

const BIOME_COLORS: Record<Biome, number> = {
  deepWater: 0x1f5f7a,
  water: 0x2f7fa3,
  sand: 0xc2a866,
  desert: 0xd8b878,
  savanna: 0x9ca350,
  grass: 0x6f9e3c,
  forest: 0x427a32,
  jungle: 0x2c6b27,
  taiga: 0x4a7158,
  rock: 0x8c8478,
  mountain: 0xc9ccd0,
  snow: 0xeaf1f6,
};
const DIRT_COLOR = 0x7d6b3a;
/** World-space Y of the water surface plane (docks rest here). */
const WATER_LEVEL = 0.16;

export class Renderer3d {
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer: THREE.WebGLRenderer;
  readonly controls: OrbitControls;
  private readonly terrain: THREE.Mesh;
  private readonly resources: ResourceLayer;
  private readonly ents = new THREE.Group();
  private readonly overlays = new THREE.Group(); // selection rings + build ghost
  private readonly effects = new THREE.Group(); // transient FX (arrows) — persists across frames
  private readonly arrows: { mesh: THREE.Object3D; from: { x: number; y: number }; to: { x: number; y: number }; t: number; dur: number; arc: number }[] = [];
  private fxClock = -1; // last draw timestamp (s) for FX dt; -1 until first draw
  private readonly meshByEid = new Map<Eid, THREE.Object3D>();
  private readonly size: number;
  private readonly mapCx: number;
  private readonly mapCz: number;
  private readonly mapRadius: number;
  private readonly mapMinX: number;
  private readonly mapMaxX: number;
  private readonly mapMinZ: number;
  private readonly mapMaxZ: number;

  selected = new Set<Eid>();
  hovered: Eid | null = null;
  /** Render-only fog of war for the local team (null on the title backdrop). */
  fog: Fog | null = null;
  private fogFrame = 0;
  private terrainBaseCol!: Float32Array; // pristine per-vertex terrain colours
  private terrainBright!: Float32Array;  // current brightness per hex (1 = lit)
  private terrainVPH = 54;               // terrain vertices per hex
  /** When on (debug overlay / F3), draw the collision "sub-hex" wireframe. */
  debugNav = false;
  buildPreview: { buildingId: string; x: number; z: number; valid: boolean; line?: { x: number; z: number; valid: boolean }[] } | null = null;

  private raycaster = new THREE.Raycaster();
  private tmpVec = new THREE.Vector3();
  private keys = new Set<string>();
  private edge = { x: 0, y: 0 };
  /** Sun light; its shadow frustum follows the camera so shadows are real-time
   *  over the VISIBLE area only (cheap) instead of baked across the whole map. */
  private sun!: THREE.DirectionalLight;
  private readonly sunDir = new THREE.Vector3(0.7, 1.1, 0.45).normalize();
  /** Camera elevation above the horizon (radians). Lower = oblique (trunks
   *  visible); set via setCamElevDeg. Default ~50° (was a near-top-down 65°). */
  private camElev = THREE.MathUtils.degToRad(35);
  /** Camera azimuth (yaw) around the look target, radians. 0 = facing +Z (the
   *  default axis-aligned view); Q/E rotate it. */
  private camAzim = 0;
  /** Low-passed ground heights the camera orbits / is floored above, so moving
   *  over terrain bumps eases the view instead of jumping per tile. -1 = uninit. */
  private camGroundY = -1;
  private camFloorY = -1;
  /** When false (e.g. the title-screen backdrop), WASD/edge-pan/Q-E camera
   *  control is ignored — the view just auto-orbits. */
  inputEnabled = true;

  constructor(world: World) {
    this.size = world.size;
    const hexes = world.hexes;
    const b = mapBounds(hexes);
    this.mapCx = b.cx;
    this.mapCz = b.cz;
    this.mapRadius = b.radius;
    const rb = rectBounds(hexes); // world edge (rectangle) for clamping the camera
    this.mapMinX = rb.minX; this.mapMaxX = rb.maxX;
    this.mapMinZ = rb.minZ; this.mapMaxZ = rb.maxZ;

    // Bright blue skybox, with a soft WHITE cloudy fog rolling in around the map
    // edges: distant terrain + sea dissolve into a low cloud/mist bank (white,
    // not blue) for an "end of the world" horizon, while the sky above stays blue.
    this.scene.background = new THREE.Color(0x9fccff);
    this.scene.fog = new THREE.Fog(0xeef3f7, b.radius * 0.9, b.radius * 1.9);

    this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: "high-performance" });
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 1.5));
    this.renderer.setSize(innerWidth, innerHeight);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.3;

    this.camera = new THREE.PerspectiveCamera(45, innerWidth / innerHeight, 0.1, 1000);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.enableRotate = false;
    this.controls.enablePan = false;
    this.controls.minDistance = 3;
    this.controls.maxDistance = THREE.MathUtils.clamp(b.radius * 0.26, 9, 15);
    this.controls.maxPolarAngle = Math.PI * 0.46;

    // lights
    this.scene.add(new THREE.HemisphereLight(0xbfe3ff, 0x5a4a32, 1.15));
    const sun = new THREE.DirectionalLight(0xfff4e0, 2.0);
    sun.castShadow = true;
    // Tight shadow map that TRACKS the camera (see updateSunShadow): a small
    // ortho frustum over the visible area means the 2k map's texels concentrate
    // where the player looks → sharp, real-time shadows (moving units included)
    // for the cost of shadowing a patch instead of the whole map every frame.
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.near = 1;
    sun.shadow.bias = -0.0004;
    this.scene.add(sun, sun.target);
    this.sun = sun;

    // static terrain + water + cloud sea + hex lines
    this.terrain = buildTerrain(hexes, this.size);
    this.terrain.receiveShadow = true;
    this.scene.add(this.terrain);
    // fog of war: keep the pristine terrain colours + a per-hex brightness so the
    // draw loop can darken out-of-vision hexes in place. Vertex groups are emitted
    // one hex at a time (hex array index == world.hkey), so VPH verts map to hex k.
    const tcol = this.terrain.geometry.attributes.color as THREE.BufferAttribute;
    this.terrainBaseCol = Float32Array.from(tcol.array as Float32Array);
    this.terrainVPH = Math.round(tcol.count / hexes.length); // verts per hex (54)
    this.terrainBright = new Float32Array(hexes.length).fill(1);
    this.scene.add(this.buildWater(hexes, this.size));
    this.scene.add(this.buildCloudTilesMesh(hexes, this.size)); // rising cloud-tile blockade ringing the map
    this.scene.add(buildHexLines(hexes, this.size));
    // static resource props as instanced pools (a few draw calls for thousands
    // of nodes), built once. Must exist before the first shadow pass so they
    // cast shadows into the one-shot shadow map.
    this.resources = new ResourceLayer(world, this.size);
    this.scene.add(this.resources.group);
    this.scene.add(this.ents);
    this.scene.add(this.overlays);
    this.scene.add(this.effects);

    this.centerOnMap();

    // shadows: entities move, so we DO need live shadows; keep autoUpdate on
    // but the map is small enough that it's affordable.

    // camera pan nav (angle stays locked)
    const navKeys: Record<string, string> = {
      KeyW: "f", ArrowUp: "f",
      KeyS: "b", ArrowDown: "b",
      KeyA: "l", ArrowLeft: "l",
      KeyD: "r", ArrowRight: "r",
      KeyQ: "yl", KeyE: "yr", // Q/E yaw the camera
    };
    const typing = (e: Event) => {
      const t = e.target as HTMLElement | null;
      return !!t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
    };
    addEventListener("keydown", (e) => {
      if (typing(e) || !this.inputEnabled) return;
      if (navKeys[e.code]) { this.keys.add(navKeys[e.code]); e.preventDefault(); }
    });
    addEventListener("keyup", (e) => { if (navKeys[e.code]) this.keys.delete(navKeys[e.code]); });
    const EDGE_PX = 24;
    this.renderer.domElement.addEventListener("mousemove", (e) => {
      if (!this.inputEnabled) return;
      this.edge.x = e.clientX < EDGE_PX ? -1 : e.clientX > innerWidth - EDGE_PX ? 1 : 0;
      this.edge.y = e.clientY < EDGE_PX ? -1 : e.clientY > innerHeight - EDGE_PX ? 1 : 0;
    });
    this.renderer.domElement.addEventListener("mouseleave", () => { this.edge.x = 0; this.edge.y = 0; });

    addEventListener("resize", () => {
      this.camera.aspect = innerWidth / innerHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setPixelRatio(Math.min(devicePixelRatio, 1.5));
      this.renderer.setSize(innerWidth, innerHeight);
    });
  }

  /** Per-frame: pan camera (cheap) + sync dynamic entities + render. */
  draw(world: World) {
    this.panStep();
    this.applyZoomTilt();
    // recompute team vision ~10Hz (vision needn't update every frame) + darken
    // terrain and resource props outside the local team's sight.
    if (this.fog && this.fogFrame++ % 6 === 0) {
      this.fog.update(world);
      this.applyTerrainFog();
      this.resources.applyFog(this.fog, world, FOG_DIM);
    }
    this.syncEntities(world);
    this.resources.update(world, this.fog ?? undefined);
    this.syncOverlays(world);
    this.updateFx(world);
    this.updateSunShadow();
    this.renderer.render(this.scene, this.camera);
  }

  private panStep() {
    if (!this.inputEnabled) { this.controls.update(); return; } // backdrop: autoRotate only
    // Q/E yaw first, so a simultaneous WASD pan resolves against the new heading.
    if (this.keys.has("yl")) this.rotateBy(0.014);
    if (this.keys.has("yr")) this.rotateBy(-0.014);
    const kx = (this.keys.has("r") ? 1 : 0) - (this.keys.has("l") ? 1 : 0) + this.edge.x;
    const kz = (this.keys.has("b") ? 1 : 0) - (this.keys.has("f") ? 1 : 0) + this.edge.y;
    if (kx === 0 && kz === 0) { this.controls.update(); return; }
    const camRight = new THREE.Vector3().setFromMatrixColumn(this.camera.matrix, 0);
    const camBack = new THREE.Vector3().setFromMatrixColumn(this.camera.matrix, 2);
    camRight.y = 0; camRight.normalize();
    camBack.y = 0; camBack.normalize();
    const dist = this.camera.position.distanceTo(this.controls.target);
    const step = (dist * 0.9) / 60;
    const len = Math.hypot(kx, kz) || 1;
    const dx = ((camRight.x * kx + camBack.x * kz) / len) * step;
    const dz = ((camRight.z * kx + camBack.z * kz) / len) * step;
    // lock the look target to the WORLD EDGE (the map rectangle) so the view can't
    // pan out past the coast into the cloud.
    const tx = THREE.MathUtils.clamp(this.controls.target.x + dx, this.mapMinX, this.mapMaxX);
    const tz = THREE.MathUtils.clamp(this.controls.target.z + dz, this.mapMinZ, this.mapMaxZ);
    this.camera.position.x += tx - this.controls.target.x;
    this.camera.position.z += tz - this.controls.target.z;
    this.controls.target.x = tx;
    this.controls.target.z = tz;
    this.controls.update();
  }

  /**
   * Re-aim the sun's shadow frustum at whatever the camera is looking at, sized
   * to the current zoom. The ortho box only spans the visible patch (capped so a
   * zoomed-out view never shadows the whole map), so the shadow map re-renders
   * every frame cheaply and shadows are REAL-TIME there — moving units included,
   * felled trees drop their shadow — instead of a frozen, smeared whole-map bake.
   * Beyond the patch there are simply no cast shadows (an accepted trade-off).
   */
  private updateSunShadow(): void {
    const t = this.controls.target;
    const dist = this.camera.position.distanceTo(t);
    // Fit the shadow frustum to the VISIBLE ground footprint, not a fixed square
    // around the target. At an oblique angle the view sees far ahead of the
    // target, so we project several screen points onto the ground and bound them
    // — this tracks any camera angle (and future yaw rotation) automatically, so
    // shadows always cover what's on screen instead of cutting off. A cap keeps
    // the far horizon out of the pass so it stays cheap.
    const cap = THREE.MathUtils.clamp(dist * 1.8, this.size * 12, this.size * 100);
    const camP = this.camera.position;
    const W = innerWidth, H = innerHeight;
    // Project each of the FOUR screen corners (+ centre) onto the ground. A
    // corner that points at the sky (low angle) contributes a far point along its
    // horizontal direction. Each is clamped to `cap` from the target. The bound
    // box of these is exactly the on-screen ground area — every corner covered.
    const corners: [number, number][] = [[0, 0], [W, 0], [W, H], [0, H], [W / 2, H / 2]];
    let minX = t.x, maxX = t.x, minZ = t.z, maxZ = t.z;
    for (const [sx, sy] of corners) {
      this.tmpVec.set((sx / W) * 2 - 1, -(sy / H) * 2 + 1, 0.5).unproject(this.camera);
      let dx = this.tmpVec.x - camP.x, dy = this.tmpVec.y - camP.y, dz = this.tmpVec.z - camP.z;
      const len = Math.hypot(dx, dy, dz) || 1; dx /= len; dy /= len; dz /= len;
      let px: number, pz: number;
      if (dy < -1e-3) { const tt = -camP.y / dy; px = camP.x + dx * tt; pz = camP.z + dz * tt; } // hits ground
      else { const hl = Math.hypot(dx, dz) || 1; px = camP.x + (dx / hl) * cap; pz = camP.z + (dz / hl) * cap; } // sky → horizontal reach
      const ex = px - t.x, ez = pz - t.z, d = Math.hypot(ex, ez);
      if (d > cap) { px = t.x + (ex / d) * cap; pz = t.z + (ez / d) * cap; }
      if (px < minX) minX = px; else if (px > maxX) maxX = px;
      if (pz < minZ) minZ = pz; else if (pz > maxZ) maxZ = pz;
    }
    const ccx = (minX + maxX) / 2, ccz = (minZ + maxZ) / 2;
    const half = Math.max((maxX - minX) / 2, (maxZ - minZ) / 2, this.size * 6) + this.size * 3;
    const cam = this.sun.shadow.camera;
    cam.left = -half; cam.right = half; cam.top = half; cam.bottom = -half;
    cam.far = half * 4 + 80;
    cam.updateProjectionMatrix();
    const dd = half * 2 + 40;
    this.sun.position.set(ccx + this.sunDir.x * dd, this.sunDir.y * dd, ccz + this.sunDir.z * dd);
    this.sun.target.position.set(ccx, 0, ccz);
    this.sun.target.updateMatrixWorld();
  }

  /** Darken terrain hexes outside the local team's vision, in place per-vertex.
   *  Only rewrites hexes whose lit/fogged state changed since last call. */
  private applyTerrainFog(): void {
    if (!this.fog) return;
    const attr = this.terrain.geometry.attributes.color as THREE.BufferAttribute;
    const arr = attr.array as Float32Array;
    const base = this.terrainBaseCol;
    const bright = this.terrainBright;
    const vph3 = this.terrainVPH * 3;
    let changed = false;
    for (let k = 0; k < bright.length; k++) {
      const target = this.fog.isHexVisible(k) ? 1 : FOG_DIM;
      if (bright[k] === target) continue;
      bright[k] = target;
      const end = k * vph3 + vph3;
      for (let i = k * vph3; i < end; i++) arr[i] = base[i] * target;
      changed = true;
    }
    if (changed) attr.needsUpdate = true;
  }

  private syncEntities(world: World) {
    const seen = new Set<Eid>();
    for (const [eid, tr] of world.transform) {
      const ident = world.ident.get(eid);
      const kind = ident?.kind ?? "unit";
      // resource nodes are rendered by the instanced ResourceLayer, not here.
      if (kind === "resource") continue;
      // fog of war: anything NOT on the local team — enemies AND neutral wildlife —
      // is hidden outright on an out-of-vision hex (the cull drops its mesh). Own/
      // ally entities are vision sources, so they're always on a visible hex.
      const visible = !this.fog || this.fog.isPosVisible(world, tr.pos.x, tr.pos.y);
      if (!visible && this.fog && !this.fog.isLocalTeam(world, eid)) {
        // Out of vision and not own/ally. A discovered enemy BUILDING stays on the
        // map frozen at its last-seen state (cartography — we still see it on the
        // mini- & main map until we re-explore); enemy/wild UNITS and never-seen
        // buildings are hidden outright.
        const ghost = kind === "building" ? this.meshByEid.get(eid) : undefined;
        if (ghost) {
          seen.add(eid);
          applyFogShade(ghost, true); // dim "remembered" look — frozen, not refreshed
        }
        continue;
      }
      seen.add(eid);
      const def = ident?.defId ?? "";
      let obj = this.meshByEid.get(eid);
      const carc = world.carcass.has(eid); // killed animal → rebuild lying down
      // walls/gates connect to neighbours, so re-mesh when that neighbourhood changes.
      const isRampart = kind === "building" && (def === "wall" || def === "gate");
      const mask = isRampart ? this.rampartDirs(world, eid).mask : 0;
      if (!obj || obj.userData.kind !== kind || obj.userData.def !== def || obj.userData.carcass !== carc || (isRampart && obj.userData.wallMask !== mask)) {
        if (obj) this.ents.remove(obj);
        obj = this.makeEntityMesh(world, eid, kind, def);
        // merge (don't overwrite) so makeUnit's `limbs` refs survive for animation
        Object.assign(obj.userData, { kind, def, eid, carcass: carc, wallMask: mask });
        this.ents.add(obj);
        this.meshByEid.set(eid, obj);
      }
      // Docks AND ships float on the WATER surface — a water hex's terrain height
      // is the sea floor (~-0.2), so anything naval must be forced to WATER_LEVEL
      // or it sinks below the waves.
      const onWater = (kind === "building" && def === "dock") || (kind === "unit" && world.ident.get(eid)?.cls === "naval");
      const y = onWater ? WATER_LEVEL : this.heightAt(tr.pos.x, tr.pos.y);
      obj.position.set(tr.pos.x, y, tr.pos.y);
      if (kind === "unit") {
        // facing = atan2(dz, dx); a +Y rotation maps local +X to (cos, -sin), so
        // negate to point the model's nose along the actual movement direction
        // (was making units appear to walk sideways).
        obj.rotation.y = -tr.facing;
        const moving = (world.move.get(eid)?.path.length ?? 0) > 0;
        let activity: "walk" | "work" | "attack" | "idle" = moving ? "walk" : "idle";
        let atkRatio = 0;
        if (!moving) {
          const cb = world.combat.get(eid);
          if (cb && cb.cooldownLeft > 0) {
            activity = "attack";
            atkRatio = cb.cooldownLeft / cb.cooldown; // 1 just struck → 0 ready
          } else {
            const g = world.gatherer.get(eid);
            if (g && g.state === "gathering" && g.node !== null) activity = "work";
            else if (world.builder.get(eid)?.foundation) activity = "work";
            else if (world.healer.get(eid)?.active === true) activity = "work"; // priest channeling a heal
          }
        }
        this.animateUnit(obj, eid, activity, atkRatio);
      } else if (kind === "animal") {
        if (world.carcass.has(eid)) {
          // dead: lie flat on one side (single clean rotation, no live facing) and
          // lift so the body rests ON the ground instead of sinking half-under it.
          obj.rotation.set(0, 0, Math.PI / 2);
          obj.position.y += this.size * 0.18;
        } else {
          obj.rotation.y = -tr.facing;
          this.animateUnit(obj, eid, (world.move.get(eid)?.path.length ?? 0) > 0 ? "walk" : "idle");
        }
      } else {
        // buildings keep their authored orientation (docks build their gangways
        // toward each shore edge in world space, so they need no rotation).
        obj.rotation.y = 0;
      }

      // foundation state (buildings)
      const b = world.building.get(eid);
      if (b) {
        const target = b.isFoundation ? Math.max(0.3, 0.3 + 0.7 * b.constructionProgress) : 1;
        obj.scale.setScalar(target);
        applyFoundationMaterial(obj, b.isFoundation);
        // team banner catches the wind
        const flag = obj.userData.flag as THREE.Object3D | undefined;
        if (flag) flag.rotation.y = Math.sin(performance.now() * 0.0018 + eid) * 0.3;
      }
      // health bar
      this.updateHealthBar(obj, world, eid);
      // fog of war: own/neutral entities on an out-of-vision hex render dimmed
      // (enemies there were already skipped above).
      if (this.fog) applyFogShade(obj, !visible);
    }
    // remove dead — but KEEP a discovered enemy building's mesh as a frozen ghost
    // while its spot is out of vision, even after the sim entity is gone (razed
    // where we can't see). It's only dropped once we re-explore and the fog forgets
    // it (we observe the empty ground).
    for (const [eid, obj] of this.meshByEid) {
      if (seen.has(eid)) continue;
      const g = this.fog?.buildingMemory.get(eid);
      if (g && this.fog && !this.fog.isPosVisible(world, g.x, g.z)) { applyFogShade(obj, true); continue; }
      this.ents.remove(obj);
      this.meshByEid.delete(eid);
    }
  }

  private syncOverlays(world: World) {
    // clear + rebuild overlays each frame (small counts)
    while (this.overlays.children.length) {
      const c = this.overlays.children.pop()!;
      (c as any).geometry?.dispose?.();
    }
    // selection rings
    for (const eid of this.selected) {
      const tr = world.transform.get(eid);
      if (!tr) continue;
      const y = this.surfaceY(tr.pos.x, tr.pos.y);
      const owner = world.owner.get(eid)?.player;
      const col = owner !== undefined ? ownerColor(world, owner) : 0xffffff;
      // tight ring hugging the unit's footprint (was a large 'halo')
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(0.22 * this.size, 0.28 * this.size, 24),
        new THREE.MeshBasicMaterial({ color: col, transparent: true, opacity: 0.7, side: THREE.DoubleSide }),
      );
      ring.rotation.x = -Math.PI / 2;
      ring.position.set(tr.pos.x, y + 0.03, tr.pos.y);
      this.overlays.add(ring);
    }
    // hover highlight — subtle white ring under the entity beneath the cursor
    // (skip if it's already selected, to avoid stacking rings).
    if (this.hovered !== null && !this.selected.has(this.hovered)) {
      const tr = world.transform.get(this.hovered);
      if (tr) {
        const y = this.surfaceY(tr.pos.x, tr.pos.y);
        const kind = world.ident.get(this.hovered)?.kind;
        const rad = kind === "building" ? 0.6 * this.size : 0.32 * this.size;
        const ring = new THREE.Mesh(
          new THREE.RingGeometry(rad, rad + 0.06 * this.size, 28),
          new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.5, side: THREE.DoubleSide }),
        );
        ring.rotation.x = -Math.PI / 2;
        ring.position.set(tr.pos.x, y + 0.04, tr.pos.y);
        this.overlays.add(ring);
      }
    }

    // order paths (units) + rally points (buildings) for the SELECTION, so the
    // player can see where things are headed / what's queued.
    for (const eid of this.selected) {
      const tr = world.transform.get(eid);
      if (!tr) continue;
      const mv = world.move.get(eid);
      if (mv && (mv.path.length || mv.queue.length)) {
        const pts: { x: number; y: number }[] = [{ x: tr.pos.x, y: tr.pos.y }, ...mv.path];
        const queueTargets: { p: { x: number; y: number }; kind: string }[] = [];
        for (const q of mv.queue) {
          const p = orderTargetPos(world, q);
          if (p) queueTargets.push({ p, kind: q.kind });
        }
        for (const qt of queueTargets) pts.push(qt.p);
        this.addPathLine(pts, 0xeaeaea);
        // marker at the active destination
        if (mv.path.length) {
          const d = mv.path[mv.path.length - 1];
          this.addMarker(d.x, d.y, mv.intent ? ORDER_COLOR[mv.intent.kind] : 0xeaeaea, false);
        }
        // markers for each queued order, numbered by color intensity
        for (const qt of queueTargets) this.addMarker(qt.p.x, qt.p.y, ORDER_COLOR[qt.kind] ?? 0xeaeaea, false);
      }
      // building rally
      const rally = world.rally.get(eid);
      if (rally) {
        const isRes = rally.resource != null && world.resource.has(rally.resource);
        let rx = rally.x, rz = rally.y;
        if (isRes) { const rt = world.transform.get(rally.resource!); if (rt) { rx = rt.pos.x; rz = rt.pos.y; } }
        const col = isRes ? 0x6fbf4c : 0xffd24a;
        this.addPathLine([{ x: tr.pos.x, y: tr.pos.y }, { x: rx, y: rz }], col);
        // ground ring only — the old "tall" post drew a vertical line shooting up
        // off the marker that read as a stray line into nothing from this angle.
        this.addMarker(rx, rz, col, false);
      }
    }

    // nav-debug "sub-hex" wireframe: the collision footprint of every prop, unit
    // and building near the camera — the obstacle world pathing actually sees.
    if (this.debugNav) {
      const t = this.controls.target;
      const range = this.size * 26;
      const near = (x: number, z: number) => Math.hypot(x - t.x, z - t.z) <= range;
      // fog: never draw debug footprints on out-of-vision hexes — they'd reveal
      // enemy units / props the player isn't supposed to know about.
      const seen = (x: number, z: number) => !this.fog || this.fog.isPosVisible(world, x, z);
      for (const eid of world.resource.keys()) {
        const id = world.ident.get(eid);
        if (id?.kind !== "resource") continue;
        const tr = world.transform.get(eid);
        if (tr && near(tr.pos.x, tr.pos.y) && seen(tr.pos.x, tr.pos.y)) this.addCircle(tr.pos.x, tr.pos.y, propRadius(id.defId, this.size), 0xffa64d);
      }
      for (const [eid, tr] of world.transform) {
        if (!world.move.has(eid) || !near(tr.pos.x, tr.pos.y) || !seen(tr.pos.x, tr.pos.y)) continue;
        this.addCircle(tr.pos.x, tr.pos.y, tr.radius, world.fauna.has(eid) ? 0xffe14d : 0x4dd2ff);
      }
      for (const [eid, b] of world.building) {
        const tr = world.transform.get(eid);
        if (tr && near(tr.pos.x, tr.pos.y) && seen(tr.pos.x, tr.pos.y)) this.addCircle(tr.pos.x, tr.pos.y, this.size * (b.size[0] >= 3 ? 0.95 : 0.5), 0xff5d5d);
      }
    }

    // build preview ghost(s) — a single ghost at the cursor, or a run of ghosts
    // along a wall line (drag-to-place).
    if (this.buildPreview) {
      const ghost = (x: number, z: number, valid: boolean) => {
        // surfaceY so a dock ghost on a water edge rides the surface, not the seabed.
        const y = this.surfaceY(x, z);
        const m = new THREE.Mesh(
          new THREE.BoxGeometry(0.7 * this.size, 0.5 * this.size, 0.7 * this.size),
          new THREE.MeshBasicMaterial({ color: valid ? 0x55ff55 : 0xff5555, transparent: true, opacity: 0.45 }),
        );
        m.position.set(x, y + 0.25 * this.size, z);
        this.overlays.add(m);
      };
      if (this.buildPreview.line) for (const p of this.buildPreview.line) ghost(p.x, p.z, p.valid);
      else ghost(this.buildPreview.x, this.buildPreview.z, this.buildPreview.valid);
    }
  }

  // ---- overlay helpers (cached materials; geometry is freed on rebuild) ----
  private olLineMats = new Map<number, THREE.LineBasicMaterial>();
  private olMeshMats = new Map<number, THREE.MeshBasicMaterial>();
  private olLineMat(c: number): THREE.LineBasicMaterial {
    let m = this.olLineMats.get(c);
    if (!m) { m = new THREE.LineBasicMaterial({ color: c, transparent: true, opacity: 0.85 }); this.olLineMats.set(c, m); }
    return m;
  }
  private olMeshMat(c: number): THREE.MeshBasicMaterial {
    let m = this.olMeshMats.get(c);
    if (!m) { m = new THREE.MeshBasicMaterial({ color: c, transparent: true, opacity: 0.85, side: THREE.DoubleSide }); this.olMeshMats.set(c, m); }
    return m;
  }
  private addPathLine(pts: { x: number; y: number }[], color: number): void {
    if (pts.length < 2) return;
    const v: number[] = [];
    for (const p of pts) v.push(p.x, this.surfaceY(p.x, p.y) + 0.12 * this.size, p.y);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(v, 3));
    this.overlays.add(new THREE.Line(geo, this.olLineMat(color)));
  }
  /** A flat wireframe ring (nav-debug collision footprint). */
  private addCircle(wx: number, wz: number, r: number, color: number): void {
    const N = 20;
    // surfaceY so a footprint ring over water sits on the surface, not the sea floor.
    const y = this.surfaceY(wx, wz) + 0.06 * this.size;
    const v: number[] = [];
    for (let i = 0; i <= N; i++) {
      const a = (i / N) * Math.PI * 2;
      v.push(wx + Math.cos(a) * r, y, wz + Math.sin(a) * r);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(v, 3));
    this.overlays.add(new THREE.Line(geo, this.olLineMat(color)));
  }
  private addMarker(wx: number, wz: number, color: number, tall: boolean): void {
    const y0 = this.surfaceY(wx, wz);
    const ring = new THREE.Mesh(new THREE.RingGeometry(0.1 * this.size, 0.17 * this.size, 16), this.olMeshMat(color));
    ring.rotation.x = -Math.PI / 2;
    ring.position.set(wx, y0 + 0.05, wz);
    this.overlays.add(ring);
    if (tall) {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.Float32BufferAttribute([wx, y0, wz, wx, y0 + 0.7 * this.size, wz], 3));
      this.overlays.add(new THREE.Line(geo, this.olLineMat(color)));
    }
  }

  // ---- transient combat FX (flying arrows) ---------------------------------
  private readonly arrowShaftMat = new THREE.MeshBasicMaterial({ color: 0x2b1d0e });
  private readonly arrowHeadMat = new THREE.MeshBasicMaterial({ color: 0xb8b8b8 });

  /** A small arrow mesh pointing along +Y (oriented to its flight each frame). */
  private makeArrow(): THREE.Object3D {
    const u = this.size;
    const g = new THREE.Group();
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.018 * u, 0.018 * u, 0.4 * u, 5), this.arrowShaftMat);
    const head = new THREE.Mesh(new THREE.ConeGeometry(0.05 * u, 0.13 * u, 6), this.arrowHeadMat);
    head.position.y = 0.26 * u;
    g.add(shaft, head);
    return g;
  }

  /**
   * Drain the sim's cosmetic event queue and animate arrows for ranged attacks.
   * Each "projectile" event spawns an arrow that flies from shooter to target on
   * a short parabolic arc, nocked to its velocity. Events are purely visual (the
   * sim applies damage instantly), so we clear the whole queue here — nothing
   * else reads it, and leaving it un-drained leaks memory.
   */
  private updateFx(world: World): void {
    const now = performance.now() / 1000;
    const dt = this.fxClock < 0 ? 0 : Math.min(0.1, now - this.fxClock);
    this.fxClock = now;

    let spawned = 0;
    for (const ev of world.events) {
      if (ev.type !== "projectile") continue;
      if (spawned++ > 40) break; // guard a one-time backlog burst
      const mesh = this.makeArrow();
      this.effects.add(mesh);
      const dist = Math.hypot(ev.to.x - ev.from.x, ev.to.y - ev.from.y);
      this.arrows.push({
        mesh,
        from: { ...ev.from },
        to: { ...ev.to },
        t: 0,
        dur: Math.max(0.12, Math.min(0.4, dist * 0.06)),
        arc: Math.min(1.0 * this.size, 0.15 * dist + 0.12 * this.size),
      });
    }
    world.events.length = 0;

    const up = new THREE.Vector3(0, 1, 0);
    const dir = new THREE.Vector3();
    for (let i = this.arrows.length - 1; i >= 0; i--) {
      const a = this.arrows[i];
      a.t += dt;
      const e = a.t / a.dur;
      if (e >= 1) {
        this.effects.remove(a.mesh);
        a.mesh.traverse((o) => (o as THREE.Mesh).geometry?.dispose?.());
        this.arrows.splice(i, 1);
        continue;
      }
      const dx = a.to.x - a.from.x;
      const dz = a.to.y - a.from.y;
      const px = a.from.x + dx * e;
      const pz = a.from.y + dz * e;
      // surfaceY so a naval bolt/shell arcs over the water surface, not the seabed.
      const gy = this.surfaceY(px, pz);
      a.mesh.position.set(px, gy + 0.32 * this.size + a.arc * Math.sin(Math.PI * e), pz);
      // orient along the path tangent (horizontal velocity + arc's vertical rate)
      dir.set(dx, a.arc * Math.PI * Math.cos(Math.PI * e), dz).normalize();
      a.mesh.quaternion.setFromUnitVectors(up, dir);
    }
  }

  /** Directions toward each adjacent wall/gate (for connecting rampart arms) plus
   *  a bitmask signature so the mesh can be rebuilt when the neighbourhood changes. */
  private rampartDirs(world: World, eid: Eid): { dirs: { x: number; z: number }[]; mask: number } {
    const b = world.building.get(eid), tr = world.transform.get(eid);
    const dirs: { x: number; z: number }[] = [];
    let mask = 0;
    if (b && tr) {
      world.neighbors(b.origin.x, b.origin.y).forEach(([nc, nr], i) => {
        const k = world.hkey(nc, nr);
        if (world.solidHexKeys.has(k) || world.gateHexKeys.has(k)) {
          const c = world.hexCenter(nc, nr);
          dirs.push({ x: (c.x - tr.pos.x) / this.size, z: (c.y - tr.pos.y) / this.size });
          mask |= 1 << i;
        }
      });
    }
    return { dirs, mask };
  }

  private makeEntityMesh(world: World, eid: Eid, kind: string, def: string): THREE.Object3D {
    const u = this.size;
    if (kind === "building") {
      const owner = world.owner.get(eid)?.player;
      const col = owner !== undefined ? ownerColor(world, owner) : 0x9c6b3f;
      // A dock is built per-instance from its actual neighbours: a gangway reaches
      // out to EVERY adjacent shore edge, so it connects to land however it sits —
      // no single-orientation guess. Directions are world-aligned (no rotation).
      if (def === "dock") {
        const b = world.building.get(eid);
        const tr = world.transform.get(eid);
        const land: { x: number; z: number }[] = [];
        const water: { x: number; z: number }[] = [];
        if (b && tr) for (const [nc, nr] of world.neighbors(b.origin.x, b.origin.y)) {
          const c = world.hexCenter(nc, nr);
          const dir = { x: (c.x - tr.pos.x) / u, z: (c.y - tr.pos.y) / u };
          (world.isWater(nc, nr) ? water : land).push(dir);
        }
        return makeDock(u, col, land, water);
      }
      // A wall/gate is built per-instance to CONNECT to adjacent walls/gates, so a
      // run reads as one continuous rampart (re-meshed when a neighbour changes).
      if (def === "wall" || def === "gate") {
        return makeRampart(u, col, this.rampartDirs(world, eid).dirs, def === "gate");
      }
      return makeBuilding(u, col, def, world.building.get(eid)?.size ?? [2, 2]);
    }
    if (kind === "animal") {
      return makeAnimal(u, def, world.carcass.has(eid));
    }
    // unit
    const owner = world.owner.get(eid)?.player;
    const col = owner !== undefined ? ownerColor(world, owner) : 0xffffff;
    const cls = world.ident.get(eid)?.cls;
    if (cls === "naval") {
      // distinct hull per ROLE, derived from components (civ ship ids vary, but a
      // fisher gathers, a transport garrisons, a cannon ship has the siege bonus).
      const t = world.gatherer.has(eid) ? "fishing"
        : world.combat.get(eid)?.bonusBuilding ? "cannon"
          : world.combat.has(eid) ? "ballista" : "transport";
      return makeShip(u, col, t);
    }
    const ranged = (world.combat.get(eid)?.range ?? 0) > 1.5;
    return makeUnit(u, col, cls, ranged);
  }

  private updateHealthBar(obj: THREE.Object3D, world: World, eid: Eid) {
    const hp = world.health.get(eid);
    if (!hp) { if (obj.userData.bar) { (obj as any).remove(obj.userData.bar); obj.userData.bar = null; } return; }
    if (hp.hp >= hp.maxHp) {
      if (obj.userData.bar) { (obj as any).remove(obj.userData.bar); obj.userData.bar = null; }
      return;
    }
    let bar = obj.userData.bar as THREE.Mesh | null;
    if (!bar) {
      bar = new THREE.Mesh(
        new THREE.PlaneGeometry(0.5 * this.size, 0.07 * this.size),
        new THREE.MeshBasicMaterial({ color: 0x33dd33, transparent: true, opacity: 0.95 }),
      );
      bar.userData.isBar = true;
      obj.add(bar);
      obj.userData.bar = bar;
    }
    const frac = Math.max(0, hp.hp / hp.maxHp);
    bar.scale.x = Math.max(0.05, frac);
    const mat = bar.material as THREE.MeshBasicMaterial;
    mat.color.setHex(frac > 0.5 ? 0x33dd33 : frac > 0.25 ? 0xddcc33 : 0xdd3333);
    bar.position.set(0, 0.85 * this.size, 0);
    // billboard the bar to face camera (rough)
    bar.lookAt(this.camera.position);
  }

  /** Limb motion: WALK swings arms+legs (diagonal gait); WORK keeps legs still
   *  and chops the arms up/down in opposite phase (gathering / building); IDLE
   *  eases everything back to rest. Leg/arm amplitudes ease independently so
   *  transitions are smooth, and phase varies per unit so crowds aren't synced. */
  private animateUnit(obj: THREE.Object3D, eid: Eid, activity: "walk" | "work" | "attack" | "idle", atkRatio = 0) {
    const limbs = obj.userData.limbs as { p: THREE.Object3D; s: number; arm: boolean; rest?: number }[] | undefined;
    if (!limbs) return;
    // ease blends between modes so transitions aren't a pop
    const ease = (key: string, target: number) => {
      const cur = (obj.userData[key] as number) ?? 0;
      const v = cur + (target - cur) * 0.18;
      obj.userData[key] = v;
      return v;
    };
    const walk = ease("walkBlend", activity === "walk" ? 1 : 0);
    const work = ease("workBlend", activity === "work" ? 1 : 0);
    const atk = ease("atkBlend", activity === "attack" ? 1 : 0);
    const sw = Math.sin(performance.now() * 0.001 * 10 + eid * 0.7);
    const FORWARD = 1.45;
    const attack = obj.userData.attack as { armIdx: number; style: string } | undefined;
    const isPriest = obj.userData.unitCls === "priest";
    for (let i = 0; i < limbs.length; i++) {
      const { p, s, arm, rest } = limbs[i];
      // a permanent rest pose (e.g. an archer holding the bow out front) is the
      // baseline that gait/work/attack motions add onto, so the bow never just
      // dangles at the unit's side when idle.
      let rot = (rest ?? 0) + sw * s * 0.6 * walk; // walk gait (legs + arms)
      if (arm) {
        if (isPriest) {
          // priest heal: raise BOTH arms upward (gentle sway) instead of the
          // gather chop. rotation.z = -π/... lifts the arm forward/up.
          rot += work * (-2.1 + sw * 0.15);
        } else {
          rot += work * (FORWARD + sw * s * 0.7); // gather/build wave (in front)
        }
        if (atk > 0 && attack) rot += atk * attackPose(i, attack, atkRatio);
      }
      p.rotation.z = rot;
    }
  }

  // ---- picking helpers ----------------------------------------------------

  /**
   * Terrain point under a screen pixel (client coords), or null.
   *
   * The terrain is a hex grid with an exact analytic height field, so we
   * intersect the camera ray with that field directly instead of raycasting
   * the ~500k-triangle terrain mesh. Brute-force mesh raycasting cost ~17ms
   * per call (no BVH); the minimap alone called this 4x/frame, capping the
   * whole game near 14fps. This is O(1): a few ray/plane steps that converge
   * because hex tops are flat.
   */
  screenToGround(clientX: number, clientY: number): THREE.Vector3 | null {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.tmpVec.set(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
      0.5,
    );
    this.tmpVec.unproject(this.camera);
    const dir = this.tmpVec.sub(this.camera.position).normalize();
    if (dir.y > -1e-4) return null; // ray not pointing at the ground
    const cam = this.camera.position;
    // Iterate: intersect ray with the horizontal plane at the current height
    // estimate, sample the hex height there, repeat. Converges in 2-3 steps.
    let y = 0;
    let x = cam.x;
    let z = cam.z;
    for (let i = 0; i < 4; i++) {
      const t = (y - cam.y) / dir.y;
      x = cam.x + dir.x * t;
      z = cam.z + dir.z * t;
      const h = this._world?.hexAtWorld(x, z)?.height ?? 0;
      if (Math.abs(h - y) < 1e-3) { y = h; break; }
      y = h;
    }
    return new THREE.Vector3(x, y, z);
  }

  /** Project a world point to client pixel coords for screen-space box select.
   *  `behind` is true when the point is behind the camera (skip those). */
  worldToScreen(x: number, y: number, z: number): { x: number; y: number; behind: boolean } {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.tmpVec.set(x, y, z).project(this.camera);
    return {
      x: rect.left + ((this.tmpVec.x + 1) / 2) * rect.width,
      y: rect.top + ((1 - this.tmpVec.y) / 2) * rect.height,
      behind: this.tmpVec.z > 1,
    };
  }

  /** Entity eid under a screen pixel (client coords), or null. */
  pickEid(clientX: number, clientY: number): Eid | null {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.tmpVec.set(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
      0.5,
    );
    this.tmpVec.unproject(this.camera);
    const dir = this.tmpVec.sub(this.camera.position).normalize();
    this.raycaster.set(this.camera.position, dir);
    // raycast units/buildings (groups) AND the instanced resource pools, then
    // return whichever entity is nearest along the ray.
    const targets = [...this.ents.children, ...this.resources.pickTargets()];
    const hits = this.raycaster.intersectObjects(targets, true);
    for (const h of hits) {
      const resEid = this.resources.eidAt(h.object, h.instanceId);
      if (resEid !== null) return resEid;
      let o: THREE.Object3D | null = h.object;
      while (o) { if (o.userData?.eid !== undefined) return o.userData.eid as Eid; o = o.parent; }
    }
    return null;
  }

  private _world?: World;
  setWorld(w: World) { this._world = w; }
  /**
   * Height at a world point = the terrain surface of the hex beneath it.
   * Terrain tops are flat per-hex prisms, so a single-hex lookup IS the exact
   * surface height. (A previous IDW blend across neighbors dipped entities
   * below the surface near water/hills; reverted.)
   */
  heightAt(x: number, z: number): number {
    const w = this._world;
    if (!w) return 0;
    return w.hexAtWorld(x, z)?.height ?? 0;
  }

  /** Surface height for OVERLAYS (paths, rings, rally markers): the water surface
   *  over a sea hex (so a ship's order line/ring rides the waves), else the
   *  terrain top. Without this, naval overlays drew at the sea-floor height and
   *  were hidden under the opaque water — the player couldn't see ship orders. */
  private surfaceY(x: number, z: number): number {
    const w = this._world;
    const h = w?.hexAtWorld(x, z);
    if (h && (h.biome === "water" || h.biome === "deepWater")) return WATER_LEVEL;
    return this.heightAt(x, z);
  }

  /** Set the camera's distance from its look target (preserving tilt + yaw). */
  setDistance(dist: number) {
    const t = this.controls.target;
    this.placeCamera(t.x, t.z, dist);
  }

  private centerOnMap() {
    this.controls.target.set(this.mapCx, 0, this.mapCz);
    // closer default in-game zoom (was *0.2 / max 16 — a touch far)
    const dist = THREE.MathUtils.clamp(this.mapRadius * 0.12, 6, 9);
    // No horizontal yaw: the camera faces straight along +Z so WASD/edge pan and
    // the minimap stay axis-aligned. Elevation (camElev) is adjustable so the
    // player can drop the angle to see tree trunks / building sides, not just tops.
    this.placeCamera(this.mapCx, this.mapCz, dist);
  }

  /** Position the camera over (tx,tz) at the given distance, current tilt + yaw.
   *  The look target rides the GROUND surface (so zooming in over a hill orbits the
   *  hilltop, not y=0 inside it), and the camera is floored above any terrain it
   *  flies over so it never clips into the ground. */
  private placeCamera(tx: number, tz: number, dist: number) {
    // temporally low-pass the orbited ground height → no per-tile jolt when moving.
    // Water samples the SURFACE (not the deep floor) so the view doesn't plunge
    // when panning out over the sea.
    const g = Math.max(this.heightAt(tx, tz), WATER_LEVEL);
    this.camGroundY = this.camGroundY < 0 ? g : this.camGroundY + (g - this.camGroundY) * 0.07;
    const ty = this.camGroundY;
    this.controls.target.set(tx, ty, tz);
    const horiz = Math.cos(this.camElev) * dist;
    this.camera.position.set(
      tx + Math.sin(this.camAzim) * horiz,
      ty + Math.sin(this.camElev) * dist,
      tz + Math.cos(this.camAzim) * horiz,
    );
    // smoothed floor so flying over a hill eases the camera up, never snaps
    const f = Math.max(this.heightAt(this.camera.position.x, this.camera.position.z), WATER_LEVEL) + this.size * 0.8;
    this.camFloorY = this.camFloorY < 0 ? f : this.camFloorY + (f - this.camFloorY) * 0.07;
    if (this.camera.position.y < this.camFloorY) this.camera.position.y = this.camFloorY;
    this.controls.update();
  }

  /** Tilt is driven by zoom: 22° when zoomed all the way in → 35° zoomed all the
   *  way out. Runs every frame in-game so the wheel/dolly retilts smoothly. */
  private applyZoomTilt() {
    if (!this.inputEnabled) return;
    const t = this.controls.target;
    const dist = this.camera.position.distanceTo(t);
    const f = THREE.MathUtils.clamp(
      (dist - this.controls.minDistance) / (this.controls.maxDistance - this.controls.minDistance), 0, 1);
    this.camElev = THREE.MathUtils.degToRad(22 + f * (35 - 22));
    this.placeCamera(t.x, t.z, dist);
  }

  /** Rotate (yaw) the camera around its look target by `rad` — wired to Q/E. */
  rotateBy(rad: number) {
    this.camAzim += rad;
    const t = this.controls.target;
    this.placeCamera(t.x, t.z, this.camera.position.distanceTo(t));
  }

  /** Camera elevation above the horizon, in degrees (lower = more oblique, shows
   *  trunks/sides; higher = top-down). Pivots around the current look target. */
  setCamElevDeg(deg: number) {
    this.camElev = THREE.MathUtils.degToRad(THREE.MathUtils.clamp(deg, 22, 82));
    const t = this.controls.target;
    this.placeCamera(t.x, t.z, this.camera.position.distanceTo(t));
  }
  getCamElevDeg(): number {
    return Math.round(THREE.MathUtils.radToDeg(this.camElev));
  }

  private buildWater(hexes: Hex[], size: number): THREE.Mesh {
    // Glossy overlay built PER WATER HEX (not a rectangle) so it follows the jagged
    // coastline exactly — a rectangular plane pokes blue slivers past the boundary
    // tiles at the map edge. Flat at WATER_LEVEL, only on water/deepWater tiles.
    const P: number[] = [];
    const isW = (h: Hex) => h.biome === "water" || h.biome === "deepWater";
    for (const h of hexes) {
      if (!isW(h)) continue;
      const c = hexCorners(h.x, h.z, size);
      for (let i = 0; i < 6; i++) {
        const a = c[i], b = c[(i + 1) % 6];
        P.push(h.x, WATER_LEVEL, h.z, b[0], WATER_LEVEL, b[1], a[0], WATER_LEVEL, a[1]);
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(P, 3));
    geo.computeVertexNormals();
    const water = new THREE.Mesh(
      geo,
      new THREE.MeshStandardMaterial({ color: 0x2f7fa3, transparent: true, opacity: 0.7, roughness: 0.15, metalness: 0.2, fog: false }),
    );
    water.receiveShadow = true;
    return water;
  }

  /** Cloud TILES. The map's hex grid simply continues outward for a few rings as
   *  "cloud" hexes — rendered exactly like terrain tiles (a top face + cliff sides
   *  on the grid). Because each is a discrete grid cell it can NEVER overlap or
   *  seep into a terrain tile; the only difference from a grass tile is a soft,
   *  multi-layer semi-transparent cloudy face. Heights are uneven and slope down &
   *  outward, dissolving into the fog. Cliffs are drawn once (only the exposed
   *  step) so transparent faces never double-blend. */
  private buildCloudTilesMesh(hexes: Hex[], size: number): THREE.Object3D {
    let cols = 0, rows = 0, minY = Infinity;
    for (const h of hexes) {
      if (h.col + 1 > cols) cols = h.col + 1;
      if (h.row + 1 > rows) rows = h.row + 1;
      if (h.height < minY) minY = h.height;
    }
    const terr = new Map<number, number>();
    for (const h of hexes) terr.set(h.col * rows + h.row, h.biome === "water" || h.biome === "deepWater" ? WATER_LEVEL : h.height);
    const RING = 24; // wide cloud perimeter that recedes far into the distance
    const SQ3 = Math.sqrt(3);

    const ringOf = (col: number, row: number) => {
      const dC = col < 0 ? -col : col >= cols ? col - cols + 1 : 0;
      const dR = row < 0 ? -row : row >= rows ? row - rows + 1 : 0;
      return Math.max(dC, dR);
    };
    const edgeH = (col: number, row: number) => {
      const cc = Math.max(0, Math.min(cols - 1, col)), rr = Math.max(0, Math.min(rows - 1, row));
      return terr.get(cc * rows + rr) ?? minY;
    };
    // inverse pixel→cell (axial rounding) → which ring/edge a world point belongs to.
    const posToCell = (px: number, pz: number): [number, number] => {
      const qf = px / (1.5 * size);
      const rf = pz / (SQ3 * size) - qf / 2;
      let x = qf, z = rf, y = -qf - rf;
      let rx = Math.round(x), ry = Math.round(y), rz = Math.round(z);
      const dx = Math.abs(rx - x), dy = Math.abs(ry - y), dz = Math.abs(rz - z);
      if (dx > dy && dx > dz) rx = -ry - rz; else if (dy > dz) ry = -rx - rz; else rz = -rx - ry;
      return [rx, rz + ((rx - (rx & 1)) >> 1)];
    };

    // The cloud is ONE continuous indexed height-field grid (every vertex genuinely
    // shared between its triangles) so it can NEVER have holes. Heights rise gently
    // outward and lump with noise → an organic white mass. Vertices over the land
    // are transparent (alpha 0) and ramp opaque outward, so it joins the coast with
    // no seam and no seeping onto the playable tiles.
    let minWX = Infinity, maxWX = -Infinity, minWZ = Infinity, maxWZ = -Infinity;
    for (const h of hexes) {
      if (h.x < minWX) minWX = h.x; if (h.x > maxWX) maxWX = h.x;
      if (h.z < minWZ) minWZ = h.z; if (h.z > maxWZ) maxWZ = h.z;
    }
    const margin = RING * size * 1.7;
    const x0 = minWX - margin, x1 = maxWX + margin, z0 = minWZ - margin, z1 = maxWZ + margin;
    const step = size * 0.8;
    const NX = Math.max(1, Math.round((x1 - x0) / step));
    const NZ = Math.max(1, Math.round((z1 - z0) / step));

    const P: number[] = [], C: number[] = [], UV: number[] = [];
    const inMapV: boolean[] = [], ringV: number[] = [];
    for (let j = 0; j <= NZ; j++) {
      for (let i = 0; i <= NX; i++) {
        const px = x0 + (i / NX) * (x1 - x0), pz = z0 + (j / NZ) * (z1 - z0);
        const [col, row] = posToCell(px, pz);
        const ring = ringOf(col, row);
        const inMap = col >= 0 && col < cols && row >= 0 && row < rows;
        const lumpFade = Math.min(1, ring / 2.5);
        const lump = ((cloudFbm(px * 0.09 + 5, pz * 0.09 + 11) - 0.5) * 0.9 +
          (cloudFbm(px * 0.22 + 30, pz * 0.22 + 17) - 0.5) * 0.35) * lumpFade;
        // SLIGHTLY fold the outer lip down over the last ~3 rings — a gentle
        // rise-then-fall so the far edge softly rolls over (not a cliff/mountain).
        const foldFrac = Math.max(0, (ring - (RING - 3)) / 3);
        const fold = foldFrac * foldFrac * 0.8;
        const y = edgeH(col, row) + Math.max(0, ring - 1) * 0.1 + lump - fold;
        const a = inMap ? 0 : Math.max(0, Math.min(1, (RING - ring + 1) / 3)) * Math.min(1, ring / 1.2) * 0.88;
        const s = 0.96 + (cloudFbm(px * 0.2 + 1, pz * 0.2 + 9) - 0.5) * 0.06;
        P.push(px, y, pz); C.push(s, s, s, a); UV.push(px * 0.05, pz * 0.05);
        inMapV.push(inMap); ringV.push(ring);
      }
    }
    const idx: number[] = [];
    const id = (i: number, j: number) => j * (NX + 1) + i;
    for (let j = 0; j < NZ; j++) {
      for (let i = 0; i < NX; i++) {
        const c = [id(i, j), id(i + 1, j), id(i, j + 1), id(i + 1, j + 1)];
        if (c.every((k) => inMapV[k])) continue;        // fully over land — skip
        if (c.every((k) => ringV[k] > RING)) continue;  // fully beyond the bank — skip
        idx.push(c[0], c[2], c[1], c[1], c[2], c[3]);
      }
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(P, 3));
    geo.setAttribute("color", new THREE.Float32BufferAttribute(C, 4));
    geo.setAttribute("uv", new THREE.Float32BufferAttribute(UV, 2));
    geo.setIndex(idx);
    geo.computeVertexNormals();
    const alphaMap = makeCloudAlphaTexture();
    const mat = new THREE.MeshStandardMaterial({
      // SMOOTH-shaded (no flatShading) continuous surface → an organic white mass,
      // not faceted hex blocks. Matte (roughness 1, no metal, low emissive) + soft
      // blurred alpha texture; double-sided so it reads from any angle.
      vertexColors: true, transparent: true, depthWrite: false, alphaMap, side: THREE.DoubleSide,
      roughness: 1, metalness: 0, emissive: 0xeef2f7, emissiveIntensity: 0.15,
    });
    // stack the same surface a few times, each lower on Y → layered cloud depth
    const group = new THREE.Group();
    for (let k = 0; k < 4; k++) {
      const m = new THREE.Mesh(geo, mat);
      m.position.y = -k * 0.4;
      group.add(m);
    }
    return group;
  }

  /**
   * Recenter the view over an entity WITHOUT changing the locked camera angle
   * or zoom. We pan camera.position by the same XZ delta as the target, so the
   * camera-to-target offset (which sets angle + distance) is preserved. Setting
   * only the target — as before — made the angle/zoom drift per seed because
   * the start hex location varies while the camera stayed at the map center.
   */
  centerOn(world: World, eid: Eid) {
    const tr = world.transform.get(eid);
    if (!tr) return;
    this.panTo(tr.pos.x, tr.pos.y);
  }

  /** Recenter the view over a world point (x, z) preserving angle + zoom. The
   *  target is clamped to the world edge (map rectangle) so the view never leaves
   *  the playable area for the surrounding cloud. */
  panTo(worldX: number, worldZ: number) {
    worldX = THREE.MathUtils.clamp(worldX, this.mapMinX, this.mapMaxX);
    worldZ = THREE.MathUtils.clamp(worldZ, this.mapMinZ, this.mapMaxZ);
    const dx = worldX - this.controls.target.x;
    const dz = worldZ - this.controls.target.z;
    this.controls.target.x += dx;
    this.controls.target.z += dz;
    this.camera.position.x += dx;
    this.camera.position.z += dz;
    this.controls.update();
  }

  /** Serializable view state (target + zoom + yaw) for save/restore across a
   *  reload. Tilt is derived from zoom (applyZoomTilt), so distance carries it. */
  getView(): { tx: number; tz: number; dist: number; azim: number } {
    const t = this.controls.target;
    return { tx: t.x, tz: t.z, dist: this.camera.position.distanceTo(t), azim: this.camAzim };
  }

  /** Restore a saved view: yaw + target + zoom (which re-derives the tilt). The
   *  target is clamped to the map, so a view saved on a bigger map stays valid. */
  setView(v: { tx: number; tz: number; dist: number; azim: number }): void {
    this.camAzim = v.azim;
    const dist = THREE.MathUtils.clamp(v.dist, this.controls.minDistance, this.controls.maxDistance);
    const tx = THREE.MathUtils.clamp(v.tx, this.mapMinX, this.mapMaxX);
    const tz = THREE.MathUtils.clamp(v.tz, this.mapMinZ, this.mapMaxZ);
    // prime the low-pass ground/floor so the first frame doesn't ease in from afar
    this.camGroundY = -1; this.camFloorY = -1;
    this.placeCamera(tx, tz, dist);
  }
}

// ---- color + mesh factories ----------------------------------------------

/** Marker/line color per order kind, for the selection's path/queue overlay. */
const ORDER_COLOR: Record<string, number> = {
  move: 0xeaeaea, attackMove: 0xff8844, attack: 0xff4444,
  gather: 0x6fbf4c, dropoff: 0x6db6c9, build: 0xffd24a,
  embark: 0x4ec0e0, unload: 0x4ec0e0,
};

/** World destination of a queued/active order (for drawing waypoints). */
function orderTargetPos(world: World, intent: MoveIntent): { x: number; y: number } | null {
  switch (intent.kind) {
    case "move":
    case "attackMove":
      return { x: intent.x, y: intent.y };
    case "attack": { const t = world.transform.get(intent.target); return t ? { x: t.pos.x, y: t.pos.y } : null; }
    case "gather": { const t = world.transform.get(intent.node); return t ? { x: t.pos.x, y: t.pos.y } : null; }
    case "dropoff": { const t = world.transform.get(intent.dropSite); return t ? { x: t.pos.x, y: t.pos.y } : null; }
    case "build": { const t = world.transform.get(intent.foundation); return t ? { x: t.pos.x, y: t.pos.y } : null; }
    case "embark": { const t = world.transform.get(intent.transport); return t ? { x: t.pos.x, y: t.pos.y } : null; }
    case "unload": return { x: intent.x, y: intent.y };
  }
}

function ownerColor(world: World, owner: number): number {
  const c = world.players.get(owner)?.color ?? "#cccccc";
  return parseInt(c.replace("#", ""), 16);
}

function applyFoundationMaterial(obj: THREE.Object3D, isFoundation: boolean) {
  obj.traverse((o) => {
    const m = (o as THREE.Mesh).material as THREE.MeshStandardMaterial | undefined;
    if (!m) return;
    if (isFoundation) {
      m.transparent = true; m.opacity = 0.55; (m as any).wireframe = true;
    } else {
      m.transparent = false; m.opacity = 1; (m as any).wireframe = false;
    }
  });
}

/** Fog-of-war shading: darken an entity's body materials when it's out of the
 *  local team's vision ("shadowed/unlit" debug fog), restore when seen. Caches the
 *  base colour per material + the current state per object to avoid redundant work. */
function applyFogShade(obj: THREE.Object3D, shaded: boolean): void {
  if (obj.userData.fogged === shaded) return;
  obj.userData.fogged = shaded;
  obj.traverse((o) => {
    const mat = (o as THREE.Mesh).material;
    if (!mat) return;
    for (const m of Array.isArray(mat) ? mat : [mat]) {
      if (!(m as { isMeshStandardMaterial?: boolean }).isMeshStandardMaterial) continue; // bodies only (skip Basic health bars)
      const std = m as THREE.MeshStandardMaterial;
      if (std.userData.baseColor === undefined) std.userData.baseColor = std.color.getHex();
      const base = std.userData.baseColor as number;
      std.color.setHex(shaded ? darkenHex(base, FOG_DIM) : base);
    }
  });
}

/** Brightness multiplier for terrain + entities + props outside the local team's vision. */
const FOG_DIM = 0.33;

function darkenHex(hex: number, f: number): number {
  const r = Math.round(((hex >> 16) & 0xff) * f);
  const g = Math.round(((hex >> 8) & 0xff) * f);
  const b = Math.round((hex & 0xff) * f);
  return (r << 16) | (g << 8) | b;
}

const STONE = 0x9a958c, PLASTER = 0xcdbfa3, WOOD = 0xb08a5a, DARKWOOD = 0x6b4a2b, DOOR = 0x3a2a1a, WINDOW = 0x2a2f38, LOG = 0x7a5230;

/** Per-type low-poly building. Neutral walls (timber/plaster/stone) with the
 *  team color on roof + banner so ownership reads without painting the whole
 *  thing one color. Distinct silhouettes per type. */
function makeBuilding(u: number, col: number, defId: string, _size: [number, number]): THREE.Group {
  const g = new THREE.Group();
  const M = (c: number) => new THREE.MeshStandardMaterial({ color: c, roughness: 1, flatShading: true, fog: false });
  const box = (w: number, h: number, d: number, c: number, x: number, y: number, z: number) => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w * u, h * u, d * u), M(c));
    m.position.set(x * u, y * u, z * u); m.castShadow = true; m.receiveShadow = true; g.add(m);
    return m;
  };
  const cone = (r: number, h: number, sides: number, c: number, y: number) => {
    const m = new THREE.Mesh(new THREE.ConeGeometry(r * u, h * u, sides), M(c));
    m.rotation.y = Math.PI / 4; m.position.y = y * u; m.castShadow = true; g.add(m);
    return m;
  };
  // pole + team banner on a pivot so it can sway in syncEntities
  const flag = (x: number, y: number, z: number, h: number) => {
    box(0.03, h, 0.03, DARKWOOD, x, y, z);
    const pivot = new THREE.Object3D();
    pivot.position.set(x * u, (y + h / 2) * u, z * u);
    const banner = new THREE.Mesh(new THREE.BoxGeometry(0.006 * u, 0.13 * u, 0.18 * u), M(col));
    banner.position.set(0, -0.02 * u, 0.1 * u); banner.castShadow = true;
    pivot.add(banner); g.add(pivot); g.userData.flag = pivot;
  };

  if (defId === "town_center") {
    box(0.78, 0.46, 0.78, STONE, 0, 0.23, 0);                 // stone base
    box(0.6, 0.22, 0.6, PLASTER, 0, 0.57, 0);                 // upper floor
    for (const dx of [-0.36, 0.36]) for (const dz of [-0.36, 0.36]) box(0.07, 0.58, 0.07, DARKWOOD, dx, 0.29, dz);
    cone(0.64, 0.34, 4, col, 0.86);                           // main roof (team)
    cone(0.2, 0.18, 4, col, 1.09);                            // cap
    box(0.02, 0.24, 0.18, DOOR, 0.39, 0.14, 0);               // door
    box(0.02, 0.1, 0.1, WINDOW, 0.39, 0.38, -0.24); box(0.02, 0.1, 0.1, WINDOW, 0.39, 0.38, 0.24);
    flag(0, 1.22, 0, 0.5);
  } else if (defId === "barracks") {
    box(0.8, 0.4, 0.62, WOOD, 0, 0.2, 0);                     // timber hall
    box(0.86, 0.06, 0.68, DARKWOOD, 0, 0.42, 0);              // flat roof
    for (let i = -2; i <= 2; i++) box(0.08, 0.1, 0.08, STONE, 0.41, 0.49, i * 0.13); // crenellations
    box(0.02, 0.26, 0.2, DOOR, 0.41, 0.15, -0.12);            // door
    // weapon rack beside
    box(0.04, 0.22, 0.04, DARKWOOD, -0.48, 0.13, -0.16); box(0.04, 0.22, 0.04, DARKWOOD, -0.48, 0.13, 0.16);
    box(0.04, 0.03, 0.38, DARKWOOD, -0.48, 0.23, 0);
    for (const dz of [-0.1, 0.1]) box(0.015, 0.3, 0.015, 0xcdd2d6, -0.48, 0.28, dz); // spears
    flag(0.42, 0.62, 0.24, 0.42);
  } else if (defId === "storage_pit") {
    box(0.5, 0.16, 0.05, WOOD, 0, 0.08, -0.24); box(0.5, 0.16, 0.05, WOOD, 0, 0.08, 0.24); // open walls
    box(0.05, 0.16, 0.5, WOOD, -0.24, 0.08, 0);
    for (let i = 0; i < 3; i++) {                              // stacked logs
      const m = new THREE.Mesh(new THREE.CylinderGeometry(0.045 * u, 0.045 * u, 0.32 * u, 6), M(LOG));
      m.rotation.z = Math.PI / 2; m.position.set(0.04 * u, (0.06 + i * 0.02) * u, (-0.08 + i * 0.08) * u); m.castShadow = true; g.add(m);
    }
    const heap = new THREE.Mesh(new THREE.ConeGeometry(0.15 * u, 0.16 * u, 6), M(STONE));
    heap.position.set(0.16 * u, 0.08 * u, 0.16 * u); heap.castShadow = true; g.add(heap);
    flag(-0.24, 0.32, -0.24, 0.3);
  } else if (defId === "field") {
    // FARMLAND: tilled soil slab, parallel furrow ridges, crop tufts in rows,
    // a little scarecrow. No tall roof — reads as a low field at a glance.
    box(0.8, 0.05, 0.8, 0x6b4e2a, 0, 0.025, 0);               // soil base
    for (let i = -2; i <= 2; i++) {
      box(0.74, 0.035, 0.05, 0x5a3f20, 0, 0.06, i * 0.15);     // furrow ridge
      // crop tufts straddling the ridge: green stalks + gold tips, in a row
      for (let j = -2; j <= 2; j++) {
        const cx = j * 0.16;
        const green = (i + j) % 2 === 0 ? 0x6fae3a : 0x86c24a;
        box(0.035, 0.1, 0.035, green, cx, 0.115, i * 0.15);    // stalk
        box(0.045, 0.03, 0.045, 0xd9c34a, cx, 0.17, i * 0.15); // gold head
      }
    }
    // scarecrow: cross of dark sticks + a pale head, at the back corner
    box(0.025, 0.34, 0.025, DARKWOOD, -0.26, 0.17, -0.26);     // post
    box(0.025, 0.025, 0.28, DARKWOOD, -0.26, 0.28, -0.26);     // crossbar
    box(0.08, 0.08, 0.08, 0xe6dcc0, -0.26, 0.37, -0.26);       // pale head
    flag(0.3, 0.28, 0.3, 0.26);
  } else if (defId === "stables") {
    // BARN with arched (team-colored) roof + open paddock fence and a horse.
    box(0.5, 0.34, 0.46, WOOD, -0.14, 0.17, 0);               // barn body
    for (const dz of [-0.1, 0.1]) {                            // gabled roof ∧
      const r = box(0.34, 0.045, 0.3, col, -0.14, 0.42, dz); r.rotation.x = dz < 0 ? -0.7 : 0.7;
    }
    box(0.02, 0.22, 0.18, DOOR, 0.115, 0.13, 0);              // barn door
    box(0.16, 0.1, 0.1, 0xc9b24a, -0.14, 0.07, 0.26);         // hay bale
    // paddock fence beside the barn (rails on posts)
    for (const px of [0.04, 0.24, 0.44]) box(0.02, 0.18, 0.02, DARKWOOD, px, 0.09, -0.34);
    box(0.42, 0.02, 0.02, DARKWOOD, 0.24, 0.13, -0.34);
    box(0.42, 0.02, 0.02, DARKWOOD, 0.24, 0.07, -0.34);
    // low-poly horse silhouette standing in the paddock
    box(0.26, 0.12, 0.1, 0x6e4a2c, 0.24, 0.2, -0.18);         // body
    box(0.07, 0.13, 0.07, 0x6e4a2c, 0.37, 0.27, -0.18);       // neck
    box(0.1, 0.07, 0.06, 0x6e4a2c, 0.42, 0.32, -0.18);        // head
    for (const lx of [0.14, 0.34]) for (const lz of [-0.22, -0.14]) box(0.04, 0.14, 0.04, 0x4a3220, lx, 0.07, lz); // legs
    flag(-0.4, 0.5, 0.18, 0.34);
  } else if (defId === "temple") {
    // STONE SHRINE: raised plinth, a row of 4 front columns, a team-colored
    // peaked roof above a triangular pediment. Reads classical/sacred.
    box(0.7, 0.08, 0.6, STONE, 0, 0.04, 0);                   // plinth
    box(0.56, 0.06, 0.5, PLASTER, 0, 0.11, 0);                // stylobate
    for (const dz of [-0.21, -0.07, 0.07, 0.21]) {            // 4 front columns
      const c = new THREE.Mesh(new THREE.CylinderGeometry(0.045 * u, 0.05 * u, 0.34 * u, 7), M(PLASTER));
      c.position.set(0.21 * u, 0.31 * u, dz * u); c.castShadow = true; g.add(c);
    }
    box(0.1, 0.34, 0.5, PLASTER, -0.2, 0.31, 0);             // rear cella wall
    box(0.5, 0.05, 0.56, STONE, 0, 0.5, 0);                  // entablature
    box(0.04, 0.06, 0.12, WINDOW, 0.21, 0.3, 0);            // shrine doorway gap
    // gabled roof resting flush on the entablature — two slabs meeting at a
    // ridge along X (a proper ∧), giving a triangular pediment over the front.
    // Mirrors the house gable (rotation.x + matching dz); the old rotation.z
    // version opened UP into a floating valley.
    for (const [rx, dz] of [[-0.7, -0.14], [0.7, 0.14]] as const) {
      const r = box(0.64, 0.05, 0.36, col, 0, 0.55, dz); r.rotation.x = rx;
    }
    flag(-0.24, 0.62, 0, 0.3);
  } else if (defId === "tower") {
    // tall stone shaft, overhanging battlement, merlon ring, team-coloured roof.
    box(0.52, 1.0, 0.52, STONE, 0, 0.5, 0);                    // shaft
    box(0.64, 0.12, 0.64, 0x8a8f94, 0, 1.04, 0);               // battlement deck
    for (const [dx, dz] of [[-0.26, -0.26], [0.26, -0.26], [-0.26, 0.26], [0.26, 0.26], [0, -0.28], [0, 0.28], [-0.28, 0], [0.28, 0]] as const)
      box(0.11, 0.15, 0.11, STONE, dx, 1.15, dz);             // merlon ring
    cone(0.44, 0.36, 4, col, 1.34);                            // roof (team)
    box(0.03, 0.2, 0.12, WINDOW, 0.27, 0.66, 0);               // arrow slit (E)
    box(0.12, 0.2, 0.03, WINDOW, 0, 0.66, 0.27);               // arrow slit (S)
    flag(0, 1.5, 0, 0.34);
  } else {
    // house: plaster cottage with a gabled roof + chimney
    box(0.46, 0.34, 0.42, PLASTER, 0, 0.17, 0);
    for (const dx of [-0.21, 0.21]) for (const dz of [-0.19, 0.19]) box(0.05, 0.34, 0.05, DARKWOOD, dx, 0.17, dz);
    // gabled roof: two slabs rising from the eaves to a ridge along X (∧)
    for (const [rx, dz] of [[-0.7, -0.12], [0.7, 0.12]] as const) {
      const r = box(0.5, 0.045, 0.3, col, 0, 0.42, dz); r.rotation.x = rx;
    }
    box(0.07, 0.22, 0.07, DARKWOOD, -0.15, 0.52, 0.13);       // chimney
    box(0.02, 0.2, 0.13, DOOR, 0.24, 0.12, 0);                // door
    box(0.02, 0.09, 0.09, WINDOW, 0.24, 0.27, -0.13);
  }
  return g;
}
type Dir = { x: number; z: number };

/** Adaptive WALL / GATE: a central block (a crenellated stone pillar, or a gate's
 *  piers + timber doors) with a rampart ARM reaching to the shared edge toward
 *  EACH adjacent wall/gate — so a run of them reads as ONE continuous, connected
 *  rampart however it turns. World-aligned (buildings aren't rotated). */
function makeRampart(u: number, col: number, dirs: Dir[], isGate: boolean): THREE.Group {
  const g = new THREE.Group();
  const M = (c: number) => new THREE.MeshStandardMaterial({ color: c, roughness: 1, flatShading: true, fog: false });
  const box = (w: number, h: number, d: number, c: number, x: number, y: number, z: number, parent: THREE.Object3D = g) => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w * u, h * u, d * u), M(c));
    m.position.set(x * u, y * u, z * u); m.castShadow = true; m.receiveShadow = true; parent.add(m); return m;
  };
  const norm = (dx: number, dz: number): [number, number] => { const L = Math.hypot(dx, dz) || 1; return [dx / L, dz / L]; };
  const aim = (dx: number, dz: number) => Math.atan2(-dz, dx); // +Y rotation: local +X → (cos,-sin)
  const CAP = 0x9aa0a6, MERLON = 0x7d7f86, TH = 0.32; // rampart thickness

  // A rampart beam from the centre out to each connected neighbour's shared edge
  // (~0.87), with merlons along its top. For a WALL the beam starts at the centre
  // (x=0) so two meet flush at the hub; for a GATE it starts OUTSIDE the piers
  // (x≈0.42) so the wall NEVER crosses the archway — the gap stays open.
  const armInner = isGate ? GATE_ARM_INNER_RATIO : 0;
  const armW = 0.92 - armInner, armCx = armInner + armW / 2;
  for (const d of dirs) {
    const arm = new THREE.Object3D(); arm.rotation.y = aim(...norm(d.x, d.z)); g.add(arm);
    box(armW, 0.46, TH, STONE, armCx, 0.23, 0, arm);          // rampart body
    box(armW + 0.04, 0.08, TH + 0.06, CAP, armCx, 0.49, 0, arm); // capstone walkway
    for (const mx of [armCx - armW * 0.32, armCx, armCx + armW * 0.32]) for (const mz of [-0.11, 0.11])
      box(0.1, 0.13, 0.1, MERLON, mx, 0.57, mz, arm);         // crenellation row
  }

  // HUB: a single block ORIENTED to the wall run (the first arm's axis) so a gate's
  // arch lines up with the wall and the merlons run ALONG it — not at a fixed angle.
  const hub = new THREE.Object3D();
  if (dirs.length) hub.rotation.y = aim(...norm(dirs[0].x, dirs[0].z));
  g.add(hub);

  if (isGate) {
    // A gatehouse arch: two PIERS flank a WIDE open passage (~0.8 across, well past a
    // unit's width), a raised lintel + battlement above, and door leaves swung OPEN
    // against the piers (so it reads as a passable gate, not a sealed wall).
    for (const px of [-GATE_ARM_INNER_RATIO, GATE_ARM_INNER_RATIO]) {
      box(GATE_PIER_HALF_WIDTH_RATIO * 2, 0.92, GATE_PIER_HALF_DEPTH_RATIO * 2, STONE, px, 0.46, 0, hub);
    }
    box(1.06, 0.18, 0.6, STONE, 0, 1.0, 0, hub);                                    // arch lintel
    box(1.1, 0.08, 0.66, CAP, 0, 1.11, 0, hub);                                     // capstone
    for (const mx of [-0.4, 0, 0.4]) box(0.13, 0.14, 0.13, MERLON, mx, 1.2, 0, hub); // battlement
    for (const px of [-0.34, 0.34]) for (const dz of [-0.22, 0.22])                  // open door leaves (against piers)
      box(0.06, 0.62, 0.16, DARKWOOD, px, 0.32, dz, hub);
    box(0.04, 0.42, 0.04, DARKWOOD, 0, 1.3, 0, hub);                                // banner pole
    box(0.02, 0.14, 0.2, col, 0, 1.37, 0.11, hub);                                  // team banner
  } else {
    box(0.4, 0.5, TH + 0.04, STONE, 0, 0.25, 0, hub);         // hub body (along the wall)
    box(0.46, 0.08, TH + 0.1, CAP, 0, 0.49, 0, hub);          // hub cap
    if (dirs.length === 0)
      for (const dx of [-0.13, 0.13]) for (const dz of [-0.11, 0.11]) box(0.1, 0.13, 0.1, MERLON, dx, 0.57, dz, hub); // lone wall: crenellate it
    else
      for (const dz of [-0.11, 0.11]) box(0.1, 0.13, 0.1, MERLON, 0, 0.57, dz, hub); // bridge the arms' merlon rows
  }
  return g;
}
/** Adaptive DOCK: a deck centred in the water hex with a gangway reaching out to
 *  EACH adjacent shore edge and railings framing the open-water edges, plus a
 *  boat moored on the most open water side. Built per-instance from the real
 *  neighbours in world-aligned local space (no rotation), so it meets whatever
 *  land it touches (1–3 shores) instead of guessing a single orientation. */
function makeDock(u: number, col: number, landDirs: Dir[], waterDirs: Dir[]): THREE.Group {
  const g = new THREE.Group();
  const M = (c: number) => new THREE.MeshStandardMaterial({ color: c, roughness: 1, flatShading: true, fog: false });
  const box = (w: number, h: number, d: number, c: number, x: number, y: number, z: number, parent: THREE.Object3D = g) => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w * u, h * u, d * u), M(c));
    m.position.set(x * u, y * u, z * u); m.castShadow = true; m.receiveShadow = true; parent.add(m); return m;
  };
  const norm = (dx: number, dz: number): [number, number] => { const L = Math.hypot(dx, dz) || 1; return [dx / L, dz / L]; };
  // A +Y rotation maps local +X to (cos, -sin) in world XZ, so aim local +X along
  // a world direction (dx,dz) with atan2(-dz, dx).
  const aim = (dx: number, dz: number) => Math.atan2(-dz, dx);

  // ---- HEXAGONAL deck aligned to the tile (corners at 60·i°, same convention as
  // the terrain hexes). Its 6 EDGES therefore face the 6 neighbour directions, so
  // gangways/railings placed in each neighbour frame sit flush on an edge instead
  // of crossing a square deck at an angle. ----
  const R = 0.56;                       // deck vertex radius (size units)
  const APO = R * Math.sqrt(3) / 2;     // centre→edge (apothem); a hex edge sits here
  const DECK_Y = 0.14;                  // deck top (local; world = WATER_LEVEL + DECK_Y·u, above water)
  const corners = hexCorners(0, 0, R * u);
  const pos: number[] = [];
  const top = DECK_Y * u, bot = 0.04 * u;
  for (let i = 0; i < 6; i++) {
    const a = corners[i], b = corners[(i + 1) % 6];
    pos.push(0, top, 0, b[0], top, b[1], a[0], top, a[1]);             // top fan
    pos.push(a[0], top, a[1], b[0], top, b[1], b[0], bot, b[1]);       // skirt
    pos.push(a[0], top, a[1], b[0], bot, b[1], a[0], bot, a[1]);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  geo.computeVertexNormals();
  const deck = new THREE.Mesh(geo, M(0xa07e4e)); deck.castShadow = true; deck.receiveShadow = true; g.add(deck);
  for (let i = -1; i <= 1; i++) box(R * 1.1, 0.012, 0.02, 0x6b4a2b, 0, DECK_Y + 0.026, i * 0.18); // plank seams
  for (const sx of [-0.22, 0.22]) for (const sz of [-0.22, 0.22]) box(0.05, 0.6, 0.05, 0x4a3520, sx, -0.18, sz); // pilings

  // ---- a gangway ramping out to every shore edge, kept ABOVE the waterline ----
  for (const d of landDirs) {
    const arm = new THREE.Object3D(); arm.rotation.y = aim(...norm(d.x, d.z)); g.add(arm);
    const plank = box(0.55, 0.05, 0.26, 0xa07e4e, APO + 0.24, DECK_Y + 0.02, 0, arm);
    plank.rotation.z = 0.14;                                           // ramp up toward the higher shore
    for (const pz of [-0.1, 0.1]) box(0.04, 0.46, 0.04, 0x4a3520, APO + 0.1, -0.05, pz, arm); // support posts
  }

  // pick the most-open water edge (most opposite the land) to moor the boat
  let boatDir: Dir | null = null;
  if (waterDirs.length) {
    const la = { x: 0, z: 0 }; for (const d of landDirs) { la.x += d.x; la.z += d.z; }
    let bestS = Infinity;
    for (const d of waterDirs) { const s = d.x * la.x + d.z * la.z; if (s < bestS) { bestS = s; boatDir = d; } }
  }
  // ---- railings flush on the other open-water edges (posts + top rail) ----
  for (const d of waterDirs) {
    if (d === boatDir) continue;
    const rail = new THREE.Object3D(); rail.rotation.y = aim(...norm(d.x, d.z)); g.add(rail);
    for (const rz of [-0.24, 0, 0.24]) box(0.025, 0.16, 0.025, DARKWOOD, APO - 0.02, DECK_Y + 0.08, rz, rail);
    box(0.025, 0.022, R, DARKWOOD, APO - 0.02, DECK_Y + 0.15, 0, rail);
  }
  // ---- moored sailboat alongside the open edge, bow pointing out to sea ----
  if (boatDir) {
    const boat = new THREE.Object3D(); boat.rotation.y = aim(...norm(boatDir.x, boatDir.z)); g.add(boat);
    box(0.4, 0.1, 0.15, DARKWOOD, APO + 0.2, 0.05, 0, boat);
    box(0.32, 0.045, 0.11, 0x5a4026, APO + 0.2, 0.1, 0, boat);
    const bow = new THREE.Mesh(new THREE.ConeGeometry(0.085 * u, 0.2 * u, 3), M(DARKWOOD));
    bow.rotation.z = -Math.PI / 2; bow.position.set((APO + 0.46) * u, 0.05 * u, 0); bow.castShadow = true; boat.add(bow);
    box(0.02, 0.3, 0.02, WOOD, APO + 0.14, 0.25, 0, boat);
    box(0.016, 0.22, 0.12, PLASTER, APO + 0.14, 0.28, 0, boat);
  }

  // team banner on a deck corner post (swaying pivot, like other buildings)
  box(0.03, 0.36, 0.03, DARKWOOD, -0.26, 0.28, -0.26);
  const pivot = new THREE.Object3D(); pivot.position.set(-0.26 * u, 0.46 * u, -0.26 * u);
  const banner = new THREE.Mesh(new THREE.BoxGeometry(0.006 * u, 0.13 * u, 0.18 * u), M(col));
  banner.position.set(0, -0.02 * u, 0.1 * u); banner.castShadow = true; pivot.add(banner); g.add(pivot); g.userData.flag = pivot;
  return g;
}

type ShipType = "fishing" | "ballista" | "cannon" | "transport";

/** Low-poly ship hull + per-role fittings. Bow points +X so `obj.rotation.y =
 *  -facing` aims the bow along travel (same convention as makeUnit). Built around
 *  local y≈0; the entity is positioned at WATER_LEVEL so the deck rides the waves
 *  and the keel dips below. NO userData.limbs → animateUnit no-ops on it. */
function makeShip(u: number, col: number, type: ShipType): THREE.Group {
  const g = new THREE.Group();
  const M = (c: number) => new THREE.MeshStandardMaterial({ color: c, roughness: 0.82, flatShading: true, fog: false });
  const HULL = 0x6b4a2b, DECK = 0x8a6a3e, DARK = 0x4a3520, SAIL = 0xece7d7, IRON = 0x44474c, CRATE = 0xa9854f;
  const box = (w: number, h: number, d: number, c: number, x: number, y: number, z: number): THREE.Mesh => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w * u, h * u, d * u), M(c));
    m.position.set(x * u, y * u, z * u); m.castShadow = true; m.receiveShadow = true; g.add(m); return m;
  };

  const len = type === "fishing" ? 0.62 : type === "ballista" ? 0.82 : type === "cannon" ? 1.02 : 0.96;
  const wid = type === "fishing" ? 0.26 : type === "ballista" ? 0.34 : type === "cannon" ? 0.42 : 0.5;
  const deckY = 0.085;

  box(len, 0.15, wid, HULL, 0, 0.0, 0);                 // hull
  box(len * 0.9, 0.03, wid * 0.84, DECK, 0, deckY, 0);  // deck planking
  // BOW: a clean low-poly prow — the hull's full-width front face tapers to a
  // single vertical stem edge ahead of it (a real boat point, not a stray pyramid).
  {
    const xf = len * 0.5, xs = len * 0.5 + 0.22;       // hull front x → stem x
    const hw = wid * 0.5, top = 0.075, bot = -0.075;   // hull half-width + height
    const stTop = 0.05, stBot = -0.055;                // stem rises a touch, dips a touch
    const V = (x: number, y: number, z: number): number[] => [x * u, y * u, z * u];
    const pos = [
      // top deck wedge
      ...V(xf, top, hw), ...V(xf, top, -hw), ...V(xs, stTop, 0),
      // port side (z>0)
      ...V(xf, top, hw), ...V(xs, stTop, 0), ...V(xs, stBot, 0),
      ...V(xf, top, hw), ...V(xs, stBot, 0), ...V(xf, bot, hw),
      // starboard side (z<0)
      ...V(xf, top, -hw), ...V(xf, bot, -hw), ...V(xs, stBot, 0),
      ...V(xf, top, -hw), ...V(xs, stBot, 0), ...V(xs, stTop, 0),
      // bottom wedge
      ...V(xf, bot, hw), ...V(xs, stBot, 0), ...V(xf, bot, -hw),
    ];
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    geo.computeVertexNormals();
    const bow = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ color: HULL, roughness: 0.82, flatShading: true, fog: false, side: THREE.DoubleSide }));
    bow.castShadow = true; g.add(bow);
  }
  // team-colour gunwale stripes down both sides
  box(len * 0.92, 0.05, 0.045, col, 0, deckY + 0.02, wid * 0.46);
  box(len * 0.92, 0.05, 0.045, col, 0, deckY + 0.02, -wid * 0.46);

  if (type === "fishing") {
    box(0.03, 0.32, 0.03, DARK, -0.06, deckY + 0.17, 0);            // mast / pole
    const netFrame = box(0.02, 0.18, 0.24, DARK, 0.2, deckY + 0.11, 0); // net hoop arm at stern
    netFrame.rotation.z = 0.5;
    box(0.16, 0.13, 0.2, 0x35697e, 0.28, deckY + 0.04, 0);         // hanging net (bluish)
    box(0.12, 0.05, wid * 0.7, CRATE, -0.16, deckY + 0.05, 0);     // catch basket
  } else if (type === "ballista") {
    box(0.14, 0.07, 0.14, DARK, 0.08, deckY + 0.05, 0);            // mount
    box(0.03, 0.03, 0.34, DARK, 0.08, deckY + 0.13, 0);           // bow arms (across Z)
    box(0.34, 0.025, 0.025, IRON, 0.18, deckY + 0.13, 0);        // loaded bolt (points +X)
    box(0.03, 0.32, 0.03, DARK, -0.18, deckY + 0.17, 0);          // small mast aft
  } else if (type === "cannon") {
    box(0.04, 0.56, 0.04, DARK, -0.08, deckY + 0.3, 0);            // mast
    box(0.022, 0.36, 0.42, SAIL, -0.08, deckY + 0.36, 0);         // square sail
    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.052 * u, 0.064 * u, 0.4 * u, 8), M(IRON));
    barrel.rotation.z = Math.PI / 2; barrel.position.set(0.32 * u, (deckY + 0.09) * u, 0); barrel.castShadow = true; g.add(barrel);
    box(0.12, 0.08, 0.14, DARK, 0.24, deckY + 0.05, 0);           // gun carriage
  } else { // transport barge — cargo crates
    const crates: [number, number, number][] = [[-0.18, 0.12, 0.16], [0.06, -0.14, 0.18], [0.22, 0.08, 0.15], [-0.02, 0.0, 0.17], [-0.3, -0.05, 0.14]];
    for (const [cx, cz, s] of crates) box(s, s, s, CRATE, cx, deckY + s * 0.5, cz);
  }

  // a small team banner on a stern pole (sways like other buildings/units)
  box(0.025, 0.26, 0.025, DARK, -len * 0.5 + 0.04, deckY + 0.14, 0);
  const pivot = new THREE.Object3D(); pivot.position.set((-len * 0.5 + 0.04) * u, (deckY + 0.27) * u, 0);
  const banner = new THREE.Mesh(new THREE.BoxGeometry(0.006 * u, 0.1 * u, 0.14 * u), M(col));
  banner.position.set(0, -0.02 * u, 0.08 * u); banner.castShadow = true; pivot.add(banner); g.add(pivot); g.userData.flag = pivot;
  return g;
}

/** Basic low-poly humanoid: legs, torso, arms, head + a small per-class accent.
 *  Forward = +X (a small nose marks the face). Arms/legs hang from shoulder/hip
 *  pivots so they can swing while walking (see animateUnit). */
function makeUnit(u: number, col: number, cls?: string, ranged = false): THREE.Group {
  const g = new THREE.Group();
  g.userData.unitCls = cls; // read in animateUnit (priest "arms up" heal pose)
  const skin = 0xddb892;
  const dark = new THREE.Color(col).multiplyScalar(0.7).getHex();
  const mat = (c: number) => new THREE.MeshStandardMaterial({ color: c, roughness: 0.95, flatShading: true, fog: false });
  const box = (w: number, h: number, d: number, color: number, x: number, y: number, z: number, shadow = false) => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w * u, h * u, d * u), mat(color));
    m.position.set(x * u, y * u, z * u);
    if (shadow) m.castShadow = true;
    g.add(m);
    return m;
  };
  // a limb that hangs from a pivot at (px,py,pz) so rotation.z swings it fore/aft
  const limb = (w: number, h: number, color: number, py: number, pz: number, px = 0) => {
    const pivot = new THREE.Object3D();
    pivot.position.set(px * u, py * u, pz * u);
    const m = new THREE.Mesh(new THREE.BoxGeometry(w * u, h * u, w * u), mat(color));
    m.position.y = -(h * u) / 2;
    pivot.add(m);
    g.add(pivot);
    return pivot;
  };

  // attach an item into a hand (bottom of an arm pivot) so it swings with it
  const hold = (pivot: THREE.Object3D, m: THREE.Object3D, x: number, y: number, z: number) => {
    m.position.set(x * u, y * u, z * u); pivot.add(m);
  };

  if (cls === "cavalry") {
    // HORSE: body, neck+head, 4 legs on hip pivots (gallop as limbs). Rider
    // torso/head on top. Ranged → bow+quiver; melee → lance forward + shield.
    box(0.34, 0.13, 0.13, 0x6e4a2c, 0, 0.34, 0, true);   // horse body
    box(0.08, 0.16, 0.08, 0x6e4a2c, 0.17, 0.42, 0, true); // neck
    box(0.13, 0.08, 0.07, 0x6e4a2c, 0.25, 0.5, 0, true);  // head
    box(0.05, 0.06, 0.02, 0x4a3220, 0.31, 0.51, 0);       // nose/snout (front +X)
    box(0.04, 0.13, 0.13, 0x3a281a, -0.18, 0.38, 0);      // tail block
    // 4 horse legs from hip pivots (gallop via limbs)
    const hl = (px: number, pz: number) => limb(0.05, 0.22, 0x4a3220, 0.27, pz, px);
    const fL = hl(0.14, -0.07), fR = hl(0.14, 0.07);
    const bL = hl(-0.14, -0.07), bR = hl(-0.14, 0.07);
    // rider torso + head sitting on top
    box(0.1, 0.15, 0.13, col, -0.02, 0.55, 0, true);     // rider torso
    box(0.1, 0.1, 0.1, skin, -0.02, 0.68, 0, true);      // rider head
    box(0.035, 0.05, 0.035, skin, 0.03, 0.68, 0);        // rider nose
    // rider arms from shoulder pivots
    const armL = limb(0.045, 0.13, dark, 0.62, -0.09);
    const armR = limb(0.045, 0.13, dark, 0.62, 0.09);
    armL.position.x = -0.02 * u; armR.position.x = -0.02 * u;
    g.userData.limbs = [
      { p: fL, s: 1, arm: false }, { p: fR, s: -1, arm: false },
      { p: bL, s: -1, arm: false }, { p: bR, s: 1, arm: false },
      { p: armL, s: -1, arm: true }, { p: armR, s: 1, arm: true },
    ];
    if (ranged) {
      const bow = new THREE.Mesh(new THREE.TorusGeometry(0.11 * u, 0.011 * u, 3, 8, Math.PI * 1.25), mat(0x6b4a2b));
      bow.rotation.z = Math.PI / 2; hold(armL, bow, 0.05, -0.12, 0);     // bow
      box(0.04, 0.12, 0.04, 0x5a3a22, -0.12, 0.6, 0.04);                 // quiver
      box(0.01, 0.14, 0.01, 0xcdd2d6, -0.12, 0.71, 0.03);               // arrows
      g.userData.limbs[4].rest = 1.2;  // left arm holds the bow out front
      g.userData.attack = { armIdx: 5, style: "draw" };
    } else {
      const lance = new THREE.Group();                                   // lance forward
      lance.add(new THREE.Mesh(new THREE.BoxGeometry(0.016 * u, 0.5 * u, 0.016 * u), mat(0x6b4a2b)));
      const tip = new THREE.Mesh(new THREE.ConeGeometry(0.028 * u, 0.08 * u, 4), mat(0xcdd2d6));
      tip.position.y = -0.29 * u; lance.add(tip);
      hold(armR, lance, 0, -0.22, 0.02);
      hold(armL, new THREE.Mesh(new THREE.BoxGeometry(0.02 * u, 0.16 * u, 0.14 * u), mat(col)), 0.05, -0.05, -0.05); // shield on the OUTER front of the arm
      g.userData.attack = { armIdx: 5, style: "jab" };
    }
    return g;
  }

  if (cls === "priest") {
    // ROBED figure: tapered robe (wider at base), hood/head, staff with a
    // bright orb on top. No armor. Arms can raise for the heal/bless gesture.
    const robe = new THREE.Mesh(new THREE.CylinderGeometry(0.08 * u, 0.16 * u, 0.32 * u, 8), mat(0xe8e4da));
    robe.position.y = 0.18 * u; robe.castShadow = true; g.add(robe);     // robe
    box(0.1, 0.06, 0.13, col, 0, 0.36, 0, true);                         // shoulder mantle (team accent)
    box(0.1, 0.1, 0.1, skin, 0, 0.45, 0, true);                          // head
    const hood = new THREE.Mesh(new THREE.ConeGeometry(0.09 * u, 0.12 * u, 6), mat(0xe8e4da));
    hood.position.y = 0.54 * u; hood.castShadow = true; g.add(hood);     // hood/cowl
    box(0.035, 0.05, 0.035, skin, 0.05, 0.45, 0);                        // nose (front +X)
    // arms (no legs swing — robe hides them; keep two arm limbs for gestures)
    const armL = limb(0.045, 0.15, 0xd8d2c4, 0.36, -0.1);
    const armR = limb(0.045, 0.15, 0xd8d2c4, 0.36, 0.1);
    g.userData.limbs = [
      { p: armL, s: -1, arm: true }, { p: armR, s: 1, arm: true },
    ];
    // staff in right hand: shaft + bright orb on top
    const staff = new THREE.Group();
    staff.add(new THREE.Mesh(new THREE.BoxGeometry(0.018 * u, 0.4 * u, 0.018 * u), mat(0x6b4a2b)));
    const orb = new THREE.Mesh(new THREE.IcosahedronGeometry(0.04 * u, 0), mat(0xfff1a8));
    orb.position.y = 0.22 * u; staff.add(orb);
    hold(armR, staff, 0, -0.12, 0.02);
    g.userData.attack = { armIdx: 1, style: "bless" };
    return g;
  }

  box(0.12, 0.18, 0.16, col, 0, 0.27, 0, true);        // torso (broad across Z)
  box(0.11, 0.11, 0.11, skin, 0, 0.43, 0, true);       // head
  box(0.04, 0.06, 0.04, skin, 0.06, 0.43, 0);          // nose (front = +X)

  // swinging limbs: pivot at hip (legs) / shoulder (arms)
  const legL = limb(0.06, 0.16, 0x3b3b46, 0.16, -0.05);
  const legR = limb(0.06, 0.16, 0x3b3b46, 0.16, 0.05);
  const armL = limb(0.05, 0.15, dark, 0.35, -0.11);
  const armR = limb(0.05, 0.15, dark, 0.35, 0.11);
  g.userData.limbs = [
    { p: legL, s: 1, arm: false }, { p: legR, s: -1, arm: false },
    { p: armL, s: -1, arm: true }, { p: armR, s: 1, arm: true },
  ];

  if (cls === "infantry") {
    const helm = new THREE.Mesh(new THREE.ConeGeometry(0.085 * u, 0.11 * u, 5), mat(0x9aa0a6));
    helm.position.y = 0.52 * u; helm.castShadow = true; g.add(helm);            // crested helmet
    const spear = new THREE.Group();                                            // spear in right hand
    spear.add(new THREE.Mesh(new THREE.BoxGeometry(0.018 * u, 0.42 * u, 0.018 * u), mat(0x6b4a2b)));
    const tip = new THREE.Mesh(new THREE.ConeGeometry(0.03 * u, 0.08 * u, 4), mat(0xcdd2d6));
    tip.position.y = -0.25 * u; spear.add(tip);
    hold(armR, spear, 0, -0.2, 0.02);
    hold(armL, new THREE.Mesh(new THREE.BoxGeometry(0.02 * u, 0.17 * u, 0.15 * u), mat(col)), 0.05, -0.06, -0.05); // shield on the OUTER front of the arm
    g.userData.attack = { armIdx: 3, style: "jab" };
  } else if (cls === "ranged") {
    box(0.13, 0.045, 0.13, 0x46612e, 0, 0.5, 0);                                 // cap
    // BOW held out front in an aiming stance. The unit faces +X, so the bow is a
    // vertical "C" (half-torus) standing in front of the chest: tips up/down,
    // its open side toward the archer, with the string and a nocked arrow
    // pointing forward. Attached to the BODY (not the swinging hand) so it always
    // reads as "drawing a bow", never a sword dangling at the side.
    const bow = new THREE.Mesh(new THREE.TorusGeometry(0.18 * u, 0.02 * u, 4, 14, Math.PI), mat(0x6b4a2b));
    bow.rotation.z = -Math.PI / 2;                                               // open side faces the archer
    bow.position.set(0.22 * u, 0.4 * u, -0.02 * u); g.add(bow);
    const string = new THREE.Mesh(new THREE.BoxGeometry(0.006 * u, 0.36 * u, 0.006 * u), mat(0xe9e4d6));
    string.position.set(0.22 * u, 0.4 * u, -0.02 * u); g.add(string);            // bowstring (chord)
    const shaft = new THREE.Mesh(new THREE.BoxGeometry(0.28 * u, 0.012 * u, 0.012 * u), mat(0x6b4a2b));
    shaft.position.set(0.34 * u, 0.4 * u, -0.02 * u); g.add(shaft);              // nocked arrow shaft
    const tip = new THREE.Mesh(new THREE.ConeGeometry(0.025 * u, 0.07 * u, 4), mat(0xcdd2d6));
    tip.rotation.z = -Math.PI / 2; tip.position.set(0.49 * u, 0.4 * u, -0.02 * u); g.add(tip); // arrowhead
    box(0.05, 0.13, 0.05, 0x5a3a22, -0.1, 0.34, 0.05);                           // quiver on back
    // arms reach the bow: left grips the handle out front, right draws the string
    g.userData.limbs[2].rest = 1.4;  // armL forward to the grip
    g.userData.limbs[3].rest = 0.75; // armR back at the string
    g.userData.attack = { armIdx: 3, style: "draw" };
  } else {
    hold(armR, new THREE.Mesh(new THREE.BoxGeometry(0.026 * u, 0.12 * u, 0.026 * u), mat(0x8a6a3a)), 0, -0.16, 0.02); // tool
    g.userData.attack = { armIdx: 3, style: "chop" };
  }
  return g;
}

/** Per-class attack pose for an arm limb. `r` = cooldownLeft/cooldown
 *  (1 right after a strike → 0 ready), so the motion fires once per swing. */
function attackPose(limbIdx: number, attack: { armIdx: number; style: string }, r: number): number {
  const weapon = limbIdx === attack.armIdx;
  if (attack.style === "draw") {
    // archer: weapon arm draws the string back (pull as it nears ready) and
    // releases on the shot. The bow arm already holds out front via its REST
    // pose, so it contributes nothing extra here (avoids a double rotation).
    return weapon ? -1.15 * (1 - r) : 0;
  }
  // jab / chop: weapon arm thrusts forward on the strike, other arm guards.
  return weapon ? 0.4 + 1.25 * r : 0.3;
}

/** Low-poly wild animal (rabbit / deer / boar), nose along +X. Quadrupeds get
 *  four leg limbs so they trot when moving; a carcass is tipped onto its side
 *  and desaturated (no animation). */
function makeAnimal(u: number, species: string, carcass: boolean): THREE.Group {
  const g = new THREE.Group();
  const shade = (c: number) => (carcass ? new THREE.Color(c).multiplyScalar(0.55).getHex() : c);
  const mat = (c: number) => new THREE.MeshStandardMaterial({ color: shade(c), roughness: 0.95, flatShading: true, fog: false });
  const box = (w: number, h: number, d: number, c: number, x: number, y: number, z: number) => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w * u, h * u, d * u), mat(c));
    m.position.set(x * u, y * u, z * u); m.castShadow = true; g.add(m); return m;
  };
  const leg = (c: number, py: number, pz: number, px: number, len: number) => {
    const pivot = new THREE.Object3D();
    pivot.position.set(px * u, py * u, pz * u);
    const m = new THREE.Mesh(new THREE.BoxGeometry(0.045 * u, len * u, 0.045 * u), mat(c));
    m.position.y = -(len * u) / 2; m.castShadow = true; pivot.add(m); g.add(pivot); return pivot;
  };
  const quad = (c: number, hipY: number, len: number, fx: number, bx: number, hz: number) => {
    const fL = leg(c, hipY, -hz, fx, len), fR = leg(c, hipY, hz, fx, len);
    const bL = leg(c, hipY, -hz, bx, len), bR = leg(c, hipY, hz, bx, len);
    g.userData.limbs = [
      { p: fL, s: 1, arm: false }, { p: fR, s: -1, arm: false },
      { p: bL, s: -1, arm: false }, { p: bR, s: 1, arm: false },
    ];
  };

  if (species === "rabbit") {
    const fur = 0xb8a890;
    box(0.15, 0.1, 0.1, fur, 0, 0.09, 0);            // body
    box(0.08, 0.08, 0.07, fur, 0.09, 0.13, 0);       // head
    box(0.02, 0.09, 0.02, fur, 0.07, 0.21, -0.025);  // ear L
    box(0.02, 0.09, 0.02, fur, 0.07, 0.21, 0.025);   // ear R
    box(0.045, 0.045, 0.045, 0xefefe6, -0.09, 0.08, 0); // puff tail
    box(0.05, 0.07, 0.11, fur, 0, 0.035, 0);         // haunches
  } else if (species === "boar") {
    const hide = 0x3b3029;
    box(0.34, 0.17, 0.17, hide, 0, 0.18, 0);         // bulky body
    box(0.08, 0.14, 0.18, hide, -0.17, 0.2, 0);      // shoulder hump
    box(0.13, 0.12, 0.12, hide, 0.21, 0.16, 0);      // head
    box(0.07, 0.06, 0.07, 0x2a221c, 0.29, 0.14, 0);  // snout
    box(0.018, 0.035, 0.018, 0xe6e2d2, 0.31, 0.15, -0.035); // tusk L
    box(0.018, 0.035, 0.018, 0xe6e2d2, 0.31, 0.15, 0.035);  // tusk R
    quad(0x2a221c, 0.11, 0.12, 0.11, -0.11, 0.07);
  } else { // deer
    const coat = 0x8a6038;
    box(0.3, 0.13, 0.12, coat, 0, 0.3, 0);           // body
    box(0.07, 0.18, 0.07, coat, 0.16, 0.37, 0);      // neck
    box(0.11, 0.08, 0.07, coat, 0.23, 0.47, 0);      // head
    box(0.04, 0.04, 0.03, 0x5b4026, 0.29, 0.46, 0);  // muzzle
    box(0.012, 0.11, 0.012, 0x6b5030, 0.21, 0.58, -0.035); // antler L
    box(0.012, 0.11, 0.012, 0x6b5030, 0.21, 0.58, 0.035);  // antler R
    box(0.035, 0.05, 0.04, 0xefe7d8, -0.16, 0.3, 0); // tail
    quad(0x6b5030, 0.25, 0.25, 0.12, -0.12, 0.06);
  }

  if (carcass) {
    g.rotation.z = Math.PI / 2;      // tipped onto its side
    g.userData.limbs = undefined;    // no trot
  }
  return g;
}

// ---- terrain (static) ----------------------------------------------------

/** Soft, wispy grayscale alpha texture for the cloud sea: thresholded fractal
 *  noise so the cloud surface dissolves into feathered wisps (no hard tile
 *  edges) — sampled as the material's alphaMap. Tiles seamlessly (wraps). */
function makeCloudAlphaTexture(): THREE.Texture {
  const S = 256;
  const cv = document.createElement("canvas");
  cv.width = cv.height = S;
  const ctx = cv.getContext("2d")!;
  const img = ctx.createImageData(S, S);
  // wrap-safe fbm: blend the noise with a copy shifted by S so opposite edges meet
  const wf = (x: number, y: number) => {
    const f = 0.03; // LOW frequency → big, gauzy, gaussian-blur-like blobs
    const a = cloudFbm(x * f, y * f);
    const b = cloudFbm((x - S) * f, y * f);
    const c = cloudFbm(x * f, (y - S) * f);
    const d = cloudFbm((x - S) * f, (y - S) * f);
    const wx = x / S, wy = y / S;
    return (a * (1 - wx) + b * wx) * (1 - wy) + (c * (1 - wx) + d * wx) * wy;
  };
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const n = wf(x, y);
      // VERY little transparency: near-opaque (floor 225, small range) with soft,
      // gently-varying blobs — a matte, blurred gauze rather than a sharp texture.
      const t = Math.max(0, Math.min(1, (n - 0.3) / 0.5));
      const a = t * t * (3 - 2 * t);
      const v = Math.round(225 + a * 30);
      const i = (y * S + x) * 4;
      img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
      img.data[i + 3] = 255;
    }
  }
  // one cheap box-blur pass → softens any remaining hard noise edges (gaussian-ish)
  const src = ctx.createImageData(S, S);
  src.data.set(img.data);
  const at = (xx: number, yy: number) => src.data[(((yy + S) % S) * S + ((xx + S) % S)) * 4];
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      let sum = 0;
      for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) sum += at(x + dx, y + dy);
      const v = sum / 25;
      const i = (y * S + x) * 4;
      img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
    }
  }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(cv);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

/** Fractal value noise (0..1) for the billowy cloud-sea displacement. */
function cloudFbm(x: number, y: number): number {
  const hash = (xi: number, yi: number) => {
    const n = Math.sin(xi * 127.1 + yi * 311.7) * 43758.5453;
    return n - Math.floor(n);
  };
  const sm = (t: number) => t * t * (3 - 2 * t);
  const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
  const vn = (px: number, py: number) => {
    const xi = Math.floor(px), yi = Math.floor(py), xf = px - xi, yf = py - yi, u = sm(xf), v = sm(yf);
    return lerp(lerp(hash(xi, yi), hash(xi + 1, yi), u), lerp(hash(xi, yi + 1), hash(xi + 1, yi + 1), u), v);
  };
  let val = 0, amp = 0.55, f = 1;
  for (let o = 0; o < 4; o++) { val += amp * vn(x * f, y * f); f *= 2; amp *= 0.5; }
  return Math.max(0, Math.min(1, val));
}

function buildTerrain(hexes: Hex[], size: number): THREE.Mesh {
  const P: number[] = [];
  const C: number[] = [];
  let minY = Infinity;
  for (const h of hexes) if (h.height < minY) minY = h.height;
  // Deeper skirt so the coastline reads as a solid island cliff, not a thin slab.
  const baseY = minY - 0.9;
  const tmp = new THREE.Color();
  const top = new THREE.Color();
  const side = new THREE.Color();
  const dirt = new THREE.Color().setHex(DIRT_COLOR);

  for (const h of hexes) {
    const corners = hexCorners(h.x, h.z, size);
    top.setHex(BIOME_COLORS[h.biome]);
    if (h.biome === "grass") top.lerp(dirt, (h.tint ?? 0) * 0.55);
    top.multiplyScalar(0.93 + pseudo(h.x, h.z) * 0.12);
    side.copy(top).lerp(dirt, 0.5).multiplyScalar(0.7);
    for (let i = 0; i < 6; i++) {
      const a = corners[i];
      const b = corners[(i + 1) % 6];
      pushTri(P, C, h.x, h.height, h.z, b[0], h.height, b[1], a[0], h.height, a[1], top, top, top);
    }
    for (let i = 0; i < 6; i++) {
      const a = corners[i];
      const b = corners[(i + 1) % 6];
      tmp.copy(top).lerp(side, 0.45);
      pushQuad(P, C,
        a[0], h.height, a[1], tmp,
        b[0], h.height, b[1], tmp,
        b[0], baseY, b[1], side,
        a[0], baseY, a[1], side);
    }
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(P, 3));
  geo.setAttribute("color", new THREE.Float32BufferAttribute(C, 3));
  geo.computeVertexNormals();
  // fog:false → the global distance-fog never washes into the terrain (it would
  // blend fog into the ground tiles near the edge). Only the cloud volume is
  // fogged, so the world stays crisp right up to a hard fog/terrain boundary.
  return new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 0.92, metalness: 0, fog: false, side: THREE.DoubleSide }));
}

function buildHexLines(hexes: Hex[], size: number): THREE.LineSegments {
  const { minX, maxX, minZ, maxZ } = rectBounds(hexes);
  const fadeW = size * 3;
  const P: number[] = [];
  const Col: number[] = [];
  for (const h of hexes) {
    const corners = hexCorners(h.x, h.z, size);
    const y = h.height + 0.002; // hug the surface (was 0.012 → visibly floated at grazing angles)
    const edgeDist = Math.min(h.x - minX, maxX - h.x, h.z - minZ, maxZ - h.z);
    const keep = smoothstep(0, fadeW, edgeDist);
    for (let i = 0; i < 6; i++) {
      const a = corners[i];
      const b = corners[(i + 1) % 6];
      P.push(a[0], y, a[1], b[0], y, b[1]);
      Col.push(keep, keep, keep, keep, keep, keep);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(P, 3));
  geo.setAttribute("color", new THREE.Float32BufferAttribute(Col, 3));
  return new THREE.LineSegments(geo, new THREE.LineBasicMaterial({ color: 0x1c2a14, vertexColors: true, transparent: true, opacity: 0.18, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 }));
}

// ---- helpers -------------------------------------------------------------

function mapBounds(hexes: Hex[]): { cx: number; cz: number; radius: number } {
  let sx = 0, sz = 0;
  for (const h of hexes) { sx += h.x; sz += h.z; }
  const size = hexes.length ? 1 : 1;
  void size;
  const cx = sx / Math.max(1, hexes.length);
  const cz = sz / Math.max(1, hexes.length);
  let radius = 0;
  for (const h of hexes) radius = Math.max(radius, Math.hypot(h.x - cx, h.z - cz));
  return { cx, cz, radius: radius + 1 };
}
function smoothstep(e0: number, e1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}
function rectBounds(hexes: Hex[]) {
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (const h of hexes) {
    if (h.x < minX) minX = h.x; if (h.x > maxX) maxX = h.x;
    if (h.z < minZ) minZ = h.z; if (h.z > maxZ) maxZ = h.z;
  }
  return { minX, maxX, minZ, maxZ };
}
function pseudo(x: number, z: number): number {
  const s = Math.sin(x * 12.9898 + z * 78.233) * 43758.5453;
  return s - Math.floor(s);
}
function pushTri(P: number[], C: number[], ax: number, ay: number, az: number, bx: number, by: number, bz: number, cx: number, cy: number, cz: number, ca: THREE.Color, cb: THREE.Color, cc: THREE.Color) {
  P.push(ax, ay, az, bx, by, bz, cx, cy, cz);
  C.push(ca.r, ca.g, ca.b, cb.r, cb.g, cb.b, cc.r, cc.g, cc.b);
}
function pushQuad(P: number[], C: number[], ax: number, ay: number, az: number, ca: THREE.Color, bx: number, by: number, bz: number, cb: THREE.Color, cx: number, cy: number, cz: number, cc: THREE.Color, dx: number, dy: number, dz: number, cd: THREE.Color) {
  P.push(ax, ay, az, bx, by, bz, cx, cy, cz);
  C.push(ca.r, ca.g, ca.b, cb.r, cb.g, cb.b, cc.r, cc.g, cc.b);
  P.push(ax, ay, az, cx, cy, cz, dx, dy, dz);
  C.push(ca.r, ca.g, ca.b, cc.r, cc.g, cc.b, cd.r, cd.g, cd.b);
}
